import { useState, useEffect, useRef, useCallback } from "react";
import img1 from "./assets/gallery-1.jpg";
import img2 from "./assets/gallery-2.jpg";
import heroClean from "./assets/music-space-hero-clean.png";
import loggedInHero from "./assets/logged-in-hero.png";
import heroTileBackground from "./assets/hero-tile-background.png";
import backgroundTrack from "./assets/background_clavecin_0.mp4";
import { apiRequest, getSseUrl } from "./api";
import { useAuth } from "./useAuth";
import { useVideoRoom } from "./useVideoRoom";

const OTHER_INSTRUMENT = "Other Instrument";
const INSTRUMENTS = ["Piano", "Guitar", "Violin", "Vocals", "Drums", OTHER_INSTRUMENT];
const INSTRUMENT_OPTIONS = ["Piano", "Guitar", "Violin", "Vocals", "Drums", OTHER_INSTRUMENT];
const DURATION_OPTIONS = ["30 min", "45 min", "60 min", "90 min"];
const APP_MODE_KEY = "music-space-app-mode";
const LANGUAGE_KEY = "music-space-language";
const DEFAULT_LANGUAGE = "en";
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";

const LANGUAGES = [
  { code: "en", label: "English", short: "EN", flag: "🇬🇧" },
  { code: "pl", label: "Polski", short: "PL", flag: "🇵🇱" },
  { code: "uk", label: "Українська", short: "UK", flag: "🇺🇦" },
  { code: "fr", label: "Français", short: "FR", flag: "🇫🇷" },
  { code: "it", label: "Italiano", short: "IT", flag: "🇮🇹" },
  { code: "sq", label: "Shqip", short: "SQ", flag: "🇦🇱" },
  { code: "de", label: "Deutsch", short: "DE", flag: "🇩🇪" },
  { code: "es", label: "Español", short: "ES", flag: "🇪🇸" },
];

const TRANSLATIONS = {
  en: {
    demo: "Demo",
    live: "Live",
    findTeachers: "Find Teachers",
    myLessons: "My Lessons",
    dashboard: "Dashboard",
    browse: "Browse",
    admin: "Admin",
    ambient: "Ambient",
    language: "Language",
    logIn: "Log in",
    logOut: "Log out",
    getStarted: "Get Started",
    useRealAccounts: "Use real accounts",
    demoMode: "Demo mode",
    liveMode: "Live mode",
    teachersAvailableNow: "teachers available right now",
    heroLineOne: "Learn Music from",
    heroLineTwo: "World-Class Teachers",
    heroCopy: "Connect instantly with available teachers or schedule lessons in advance.",
    heroCopyInstruments: "Piano, Guitar, Violin, Vocals and more.",
    exploreDemo: "Explore Demo",
    startLearningFree: "Start Learning Free",
    browseTeachers: "Browse Teachers",
    playAmbientMusic: "Play ambient music",
    browseByInstrument: "Browse by Instrument",
    findRightTeacher: "Find the right teacher for your instrument",
    available: "available",
    howItWorks: "How It Works",
    findATeacher: "Find a Teacher",
    findTeacherDesc: "Browse by instrument, level, and language",
    connectInstantly: "Connect Instantly",
    connectInstantlyDesc: "Click 'Connect Now' for immediate video sessions",
    bookAhead: "Or Book Ahead",
    bookAheadDesc: "Schedule with the built-in calendar system",
    trackGrowth: "Track Growth",
    trackGrowthDesc: "Follow programs and monitor your progress",
  },
  pl: {
    demo: "Demo",
    live: "Live",
    findTeachers: "Znajdź nauczycieli",
    myLessons: "Moje lekcje",
    dashboard: "Panel",
    browse: "Przeglądaj",
    admin: "Admin",
    ambient: "Ambient",
    language: "Język",
    logIn: "Zaloguj",
    logOut: "Wyloguj",
    getStarted: "Zacznij",
    useRealAccounts: "Użyj kont",
    demoMode: "Tryb demo",
    liveMode: "Tryb live",
    teachersAvailableNow: "nauczycieli dostępnych teraz",
    heroLineOne: "Ucz się muzyki od",
    heroLineTwo: "świetnych nauczycieli",
    heroCopy: "Połącz się od razu z dostępnymi nauczycielami albo zaplanuj lekcję.",
    heroCopyInstruments: "Piano, gitara, skrzypce, wokal i więcej.",
    exploreDemo: "Zobacz demo",
    startLearningFree: "Zacznij za darmo",
    browseTeachers: "Przeglądaj nauczycieli",
    playAmbientMusic: "Włącz muzykę tła",
    browseByInstrument: "Przeglądaj instrumenty",
    findRightTeacher: "Znajdź właściwego nauczyciela dla swojego instrumentu",
    available: "dostępnych",
    howItWorks: "Jak to działa",
    findATeacher: "Znajdź nauczyciela",
    findTeacherDesc: "Szukaj po instrumencie, poziomie i języku",
    connectInstantly: "Połącz się od razu",
    connectInstantlyDesc: "Kliknij 'Connect Now', aby zacząć sesję wideo",
    bookAhead: "Albo zarezerwuj termin",
    bookAheadDesc: "Zaplanuj lekcję we wbudowanym kalendarzu",
    trackGrowth: "Śledź postępy",
    trackGrowthDesc: "Realizuj programy i obserwuj rozwój",
  },
  uk: {
    demo: "Демо",
    live: "Наживо",
    findTeachers: "Знайти викладачів",
    myLessons: "Мої уроки",
    dashboard: "Панель",
    browse: "Огляд",
    admin: "Адмін",
    ambient: "Фон",
    language: "Мова",
    logIn: "Увійти",
    logOut: "Вийти",
    getStarted: "Почати",
    useRealAccounts: "Справжні акаунти",
    demoMode: "Демо режим",
    liveMode: "Живий режим",
    teachersAvailableNow: "викладачів доступні зараз",
    heroLineOne: "Вивчайте музику з",
    heroLineTwo: "першокласними викладачами",
    heroCopy: "Підключайтеся до доступних викладачів одразу або плануйте уроки заздалегідь.",
    heroCopyInstruments: "Фортепіано, гітара, скрипка, вокал та інше.",
    exploreDemo: "Переглянути демо",
    startLearningFree: "Почати безкоштовно",
    browseTeachers: "Огляд викладачів",
    playAmbientMusic: "Увімкнути фонову музику",
    browseByInstrument: "Огляд за інструментом",
    findRightTeacher: "Знайдіть викладача для свого інструмента",
    available: "доступні",
    howItWorks: "Як це працює",
    findATeacher: "Знайдіть викладача",
    findTeacherDesc: "Шукайте за інструментом, рівнем і мовою",
    connectInstantly: "Підключіться одразу",
    connectInstantlyDesc: "Натисніть 'Connect Now' для відеосесії",
    bookAhead: "Або забронюйте",
    bookAheadDesc: "Плануйте уроки у вбудованому календарі",
    trackGrowth: "Стежте за прогресом",
    trackGrowthDesc: "Проходьте програми та відстежуйте розвиток",
  },
  fr: {
    demo: "Démo",
    live: "Live",
    findTeachers: "Trouver des profs",
    myLessons: "Mes cours",
    dashboard: "Tableau",
    browse: "Explorer",
    admin: "Admin",
    ambient: "Ambiance",
    language: "Langue",
    logIn: "Connexion",
    logOut: "Déconnexion",
    getStarted: "Commencer",
    useRealAccounts: "Comptes réels",
    demoMode: "Mode démo",
    liveMode: "Mode live",
    teachersAvailableNow: "profs disponibles maintenant",
    heroLineOne: "Apprenez la musique avec",
    heroLineTwo: "des profs d'exception",
    heroCopy: "Connectez-vous instantanément aux profs disponibles ou planifiez vos cours.",
    heroCopyInstruments: "Piano, guitare, violon, chant et plus.",
    exploreDemo: "Voir la démo",
    startLearningFree: "Commencer gratuitement",
    browseTeachers: "Explorer les profs",
    playAmbientMusic: "Lancer la musique d'ambiance",
    browseByInstrument: "Explorer par instrument",
    findRightTeacher: "Trouvez le bon prof pour votre instrument",
    available: "disponibles",
    howItWorks: "Comment ça marche",
    findATeacher: "Trouver un prof",
    findTeacherDesc: "Filtrez par instrument, niveau et langue",
    connectInstantly: "Connexion instantanée",
    connectInstantlyDesc: "Cliquez sur 'Connect Now' pour une session vidéo",
    bookAhead: "Ou réservez",
    bookAheadDesc: "Planifiez avec le calendrier intégré",
    trackGrowth: "Suivre les progrès",
    trackGrowthDesc: "Suivez vos programmes et votre évolution",
  },
  it: {
    demo: "Demo",
    live: "Live",
    findTeachers: "Trova insegnanti",
    myLessons: "Le mie lezioni",
    dashboard: "Dashboard",
    browse: "Sfoglia",
    admin: "Admin",
    ambient: "Ambiente",
    language: "Lingua",
    logIn: "Accedi",
    logOut: "Esci",
    getStarted: "Inizia",
    useRealAccounts: "Account reali",
    demoMode: "Modalità demo",
    liveMode: "Modalità live",
    teachersAvailableNow: "insegnanti disponibili ora",
    heroLineOne: "Impara musica con",
    heroLineTwo: "insegnanti eccellenti",
    heroCopy: "Connettiti subito con insegnanti disponibili o programma le lezioni.",
    heroCopyInstruments: "Piano, chitarra, violino, canto e altro.",
    exploreDemo: "Esplora demo",
    startLearningFree: "Inizia gratis",
    browseTeachers: "Sfoglia insegnanti",
    playAmbientMusic: "Avvia musica ambient",
    browseByInstrument: "Sfoglia per strumento",
    findRightTeacher: "Trova l'insegnante giusto per il tuo strumento",
    available: "disponibili",
    howItWorks: "Come funziona",
    findATeacher: "Trova un insegnante",
    findTeacherDesc: "Cerca per strumento, livello e lingua",
    connectInstantly: "Connettiti subito",
    connectInstantlyDesc: "Clicca 'Connect Now' per una sessione video",
    bookAhead: "Oppure prenota",
    bookAheadDesc: "Programma con il calendario integrato",
    trackGrowth: "Segui i progressi",
    trackGrowthDesc: "Segui programmi e monitora la crescita",
  },
  sq: {
    demo: "Demo",
    live: "Live",
    findTeachers: "Gjej mësues",
    myLessons: "Mësimet e mia",
    dashboard: "Paneli",
    browse: "Shfleto",
    admin: "Admin",
    ambient: "Ambient",
    language: "Gjuha",
    logIn: "Hyr",
    logOut: "Dil",
    getStarted: "Fillo",
    useRealAccounts: "Llogari reale",
    demoMode: "Modalitet demo",
    liveMode: "Modalitet live",
    teachersAvailableNow: "mësues të disponueshëm tani",
    heroLineOne: "Mëso muzikë me",
    heroLineTwo: "mësues të shkëlqyer",
    heroCopy: "Lidhu menjëherë me mësues të disponueshëm ose planifiko mësime.",
    heroCopyInstruments: "Piano, kitarë, violinë, vokal dhe më shumë.",
    exploreDemo: "Shiko demon",
    startLearningFree: "Fillo falas",
    browseTeachers: "Shfleto mësues",
    playAmbientMusic: "Luaj muzikë ambienti",
    browseByInstrument: "Shfleto sipas instrumentit",
    findRightTeacher: "Gjej mësuesin e duhur për instrumentin tënd",
    available: "të disponueshëm",
    howItWorks: "Si funksionon",
    findATeacher: "Gjej një mësues",
    findTeacherDesc: "Kërko sipas instrumentit, nivelit dhe gjuhës",
    connectInstantly: "Lidhu menjëherë",
    connectInstantlyDesc: "Kliko 'Connect Now' për seanca video",
    bookAhead: "Ose rezervo",
    bookAheadDesc: "Planifiko me kalendarin e integruar",
    trackGrowth: "Ndiq progresin",
    trackGrowthDesc: "Ndiq programet dhe zhvillimin tënd",
  },
  de: {
    demo: "Demo",
    live: "Live",
    findTeachers: "Lehrkräfte finden",
    myLessons: "Meine Stunden",
    dashboard: "Dashboard",
    browse: "Durchsuchen",
    admin: "Admin",
    ambient: "Ambient",
    language: "Sprache",
    logIn: "Einloggen",
    logOut: "Ausloggen",
    getStarted: "Loslegen",
    useRealAccounts: "Echte Konten",
    demoMode: "Demo-Modus",
    liveMode: "Live-Modus",
    teachersAvailableNow: "Lehrkräfte jetzt verfügbar",
    heroLineOne: "Lerne Musik mit",
    heroLineTwo: "erstklassigen Lehrkräften",
    heroCopy: "Verbinde dich sofort mit verfügbaren Lehrkräften oder plane Stunden im Voraus.",
    heroCopyInstruments: "Klavier, Gitarre, Violine, Gesang und mehr.",
    exploreDemo: "Demo ansehen",
    startLearningFree: "Kostenlos starten",
    browseTeachers: "Lehrkräfte ansehen",
    playAmbientMusic: "Ambient-Musik abspielen",
    browseByInstrument: "Nach Instrument suchen",
    findRightTeacher: "Finde die passende Lehrkraft für dein Instrument",
    available: "verfügbar",
    howItWorks: "So funktioniert es",
    findATeacher: "Lehrkraft finden",
    findTeacherDesc: "Suche nach Instrument, Niveau und Sprache",
    connectInstantly: "Sofort verbinden",
    connectInstantlyDesc: "Klicke auf 'Connect Now' für Videosessions",
    bookAhead: "Oder buchen",
    bookAheadDesc: "Plane mit dem integrierten Kalender",
    trackGrowth: "Fortschritt verfolgen",
    trackGrowthDesc: "Folge Programmen und beobachte deinen Fortschritt",
  },
  es: {
    demo: "Demo",
    live: "Live",
    findTeachers: "Buscar profes",
    myLessons: "Mis clases",
    dashboard: "Panel",
    browse: "Explorar",
    admin: "Admin",
    ambient: "Ambiente",
    language: "Idioma",
    logIn: "Entrar",
    logOut: "Salir",
    getStarted: "Empezar",
    useRealAccounts: "Cuentas reales",
    demoMode: "Modo demo",
    liveMode: "Modo live",
    teachersAvailableNow: "profes disponibles ahora",
    heroLineOne: "Aprende música con",
    heroLineTwo: "profes excelentes",
    heroCopy: "Conéctate al instante con profes disponibles o agenda clases con antelación.",
    heroCopyInstruments: "Piano, guitarra, violín, voz y más.",
    exploreDemo: "Ver demo",
    startLearningFree: "Empieza gratis",
    browseTeachers: "Explorar profes",
    playAmbientMusic: "Reproducir música ambiente",
    browseByInstrument: "Explorar por instrumento",
    findRightTeacher: "Encuentra el profe ideal para tu instrumento",
    available: "disponibles",
    howItWorks: "Cómo funciona",
    findATeacher: "Encuentra un profe",
    findTeacherDesc: "Busca por instrumento, nivel e idioma",
    connectInstantly: "Conecta al instante",
    connectInstantlyDesc: "Pulsa 'Connect Now' para sesiones de video",
    bookAhead: "O reserva",
    bookAheadDesc: "Agenda con el calendario integrado",
    trackGrowth: "Sigue tu progreso",
    trackGrowthDesc: "Sigue programas y monitorea tu avance",
  },
};

const TEACHERS = [
  { id: 1, name: "Anna Kowalska", av: "AK", instruments: ["Piano", "Vocals"], level: "Advanced", lang: "Polish", rating: 4.9, reviews: 128, price: 80, status: "available", bio: "Professional pianist with 15 years of teaching experience. Graduate of the Chopin Music Academy.", tags: ["Classical", "Jazz", "Improvisation"] },
  { id: 2, name: "Marco Rossi", av: "MR", instruments: ["Guitar", "Bass"], level: "Intermediate", lang: "English", rating: 4.7, reviews: 96, price: 65, status: "available", bio: "Guitar virtuoso specializing in rock and blues. Touring musician turned dedicated teacher.", tags: ["Rock", "Blues", "Fingerstyle"] },
  { id: 3, name: "Sophie Chen", av: "SC", instruments: ["Violin"], level: "Advanced", lang: "English", rating: 5.0, reviews: 214, price: 95, status: "busy", bio: "Award-winning violinist. Former first chair in the Warsaw Philharmonic.", tags: ["Classical", "Chamber", "Baroque"] },
  { id: 4, name: "Jakub Wiśniewski", av: "JW", instruments: ["Drums", "Piano"], level: "Beginner", lang: "Polish", rating: 4.6, reviews: 54, price: 55, status: "offline", bio: "Energetic drum and rhythm specialist. Great with beginners and kids.", tags: ["Rhythm", "Pop", "World Music"] },
  { id: 5, name: "Elena Vasquez", av: "EV", instruments: ["Vocals", "Guitar"], level: "Advanced", lang: "Spanish", rating: 4.8, reviews: 177, price: 75, status: "available", bio: "Opera-trained vocalist. Breath control and contemporary styles specialist.", tags: ["Opera", "Pop", "Breathing"] },
  { id: 6, name: "Tomasz Nowak", av: "TN", instruments: [OTHER_INSTRUMENT, "Piano"], level: "Professional", lang: "Polish", rating: 4.9, reviews: 89, price: 110, status: "busy", bio: "Jazz specialist with recordings on international labels.", tags: ["Jazz", "Bebop", "Composition"] },
];

const LESSONS = [
  { id: 1, title: "Piano Scales & Arpeggios", date: "Apr 18", time: "10:00", teacher: "Anna Kowalska", instrument: "Piano", status: "upcoming" },
  { id: 2, title: "Guitar Chord Progressions", date: "Apr 20", time: "14:00", teacher: "Marco Rossi", instrument: "Guitar", status: "upcoming" },
  { id: 3, title: "Music Theory Basics", date: "Apr 12", time: "11:00", teacher: "Sophie Chen", instrument: "Violin", status: "completed" },
  { id: 4, title: "Vocal Warm-Up Techniques", date: "Apr 8", time: "09:00", teacher: "Elena Vasquez", instrument: "Vocals", status: "completed" },
];

const COLLAGE_ASPECT_RATIO = "3 / 2";
const HOME_GALLERY_FRAMES = [
  { id: "g1-1", src: img1, cropX: 0, cropY: 0, label: "Listening Lounge", desc: "Friends tuning into new sounds together" },
  { id: "g1-2", src: img1, cropX: 1, cropY: 0, label: "Acoustic Jam", desc: "Casual sessions that feel warm and alive" },
  { id: "g1-3", src: img1, cropX: 0, cropY: 1, label: "Vocal Energy", desc: "Practice that builds confidence on stage" },
  { id: "g1-4", src: img1, cropX: 1, cropY: 1, label: "Late Night Replay", desc: "Music moments you want to return to" },
  { id: "g2-1", src: img2, cropX: 0, cropY: 0, label: "Piano Duet", desc: "Guided sessions with hands-on support" },
  { id: "g2-2", src: img2, cropX: 1, cropY: 0, label: "Violin Pairing", desc: "Technique and timing refined together" },
];

const FLOATING_NOTES = ["♪", "♫", "♬", "♩", "♭", "♯", "𝄞"];
const HERO_NOTE_PATH = "M123 222c0-23 20-42 46-42 7 0 14 1 20 4V78c0-25 18-45 43-48l100-13c26-3 49 18 49 45v139c0 32-27 58-62 58-37 0-67-25-67-56 0-32 29-57 65-57 13 0 25 3 35 10V77L212 93v119c0 24-21 43-48 43-24 0-41-14-41-33Z";
const INSTRUMENT_FRAME_MAP = {
  Piano: HOME_GALLERY_FRAMES[4],
  Guitar: HOME_GALLERY_FRAMES[1],
  Bass: HOME_GALLERY_FRAMES[1],
  Violin: HOME_GALLERY_FRAMES[5],
  Vocals: HOME_GALLERY_FRAMES[2],
  Drums: HOME_GALLERY_FRAMES[1],
  [OTHER_INSTRUMENT]: HOME_GALLERY_FRAMES[5],
};

function getDurationMinutes(duration) {
  const match = String(duration || "").match(/\d+/);
  return match ? Number(match[0]) : 0;
}

function HeroMusicNoteIcon({ size = 86, compact = false }) {
  const viewBox = compact ? "90 0 320 280" : "70 0 340 280";
  const paths = compact ? (
    <>
      <path d={HERO_NOTE_PATH} />
      <path d="M214 88 352 72" fill="none" stroke="#f8fbff" strokeWidth="6" strokeLinecap="round" opacity=".4" />
    </>
  ) : (
    <>
      <path d={HERO_NOTE_PATH} />
      <path d="M214 88 352 72" fill="none" stroke="#f8fbff" strokeWidth="6" strokeLinecap="round" opacity=".4" />
    </>
  );
  return (
    <svg width={size} height={size} viewBox={viewBox} aria-hidden="true" style={{ display: "block", overflow: "visible" }}>
      <defs>
        <linearGradient id={compact ? "hero-note-small-gradient" : "hero-note-large-gradient"} x1="100" y1="22" x2="374" y2="250" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#ff56ff" />
          <stop offset="0.52" stopColor="#9757ff" />
          <stop offset="1" stopColor="#00d4ff" />
        </linearGradient>
        <filter id={compact ? "hero-note-small-glow" : "hero-note-large-glow"} x="-32%" y="-30%" width="164%" height="168%">
          <feDropShadow dx="0" dy="5" stdDeviation="4" floodColor="#a855f7" floodOpacity="0.55" />
          <feDropShadow dx="0" dy="0" stdDeviation="4" floodColor="#00c8ff" floodOpacity="0.48" />
        </filter>
      </defs>
      <g fill={`url(#${compact ? "hero-note-small-gradient" : "hero-note-large-gradient"})`} filter={`url(#${compact ? "hero-note-small-glow" : "hero-note-large-glow"})`}>
        {paths}
      </g>
    </svg>
  );
}

function MusicSpaceHeroLogo() {
  return (
    <svg viewBox="0 0 820 292" role="img" aria-label="Music Space" style={{ width: "clamp(300px, 68vw, 660px)", height: "auto", display: "block", overflow: "visible" }}>
      <defs>
        <linearGradient id="hero-logo-note-gradient" x1="68" y1="32" x2="360" y2="258" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#ff67ff" />
          <stop offset="0.45" stopColor="#9a5cff" />
          <stop offset="1" stopColor="#00d2ff" />
        </linearGradient>
        <linearGradient id="hero-logo-word-gradient" x1="410" y1="78" x2="760" y2="232" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#ffffff" />
          <stop offset="0.7" stopColor="#f8fafc" />
          <stop offset="1" stopColor="#f9e9dc" />
        </linearGradient>
        <filter id="hero-logo-glow" x="-26%" y="-28%" width="158%" height="166%">
          <feDropShadow dx="0" dy="17" stdDeviation="13" floodColor="#020617" floodOpacity="0.58" />
          <feDropShadow dx="0" dy="0" stdDeviation="7" floodColor="#a855f7" floodOpacity="0.32" />
          <feDropShadow dx="0" dy="0" stdDeviation="5" floodColor="#06b6d4" floodOpacity="0.2" />
        </filter>
      </defs>
      <g filter="url(#hero-logo-glow)">
        <path
          fill="url(#hero-logo-note-gradient)"
          d={HERO_NOTE_PATH}
        />
        <path d="M214 88 352 72" fill="none" stroke="#ffffff" strokeWidth="6" strokeLinecap="round" opacity=".33" />
        <text x="412" y="124" fill="url(#hero-logo-word-gradient)" fontFamily="'Arial Black', 'Segoe UI', Arial, sans-serif" fontWeight="900" fontSize="82" letterSpacing="0">Music</text>
        <text x="412" y="214" fill="url(#hero-logo-word-gradient)" fontFamily="'Arial Black', 'Segoe UI', Arial, sans-serif" fontWeight="900" fontSize="82" letterSpacing="0">Space</text>
      </g>
    </svg>
  );
}

function HeroBenefitIcon({ type }) {
  const common = {
    fill: "none",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    strokeWidth: 3.4,
  };
  if (type === "teachers") {
    return (
      <svg viewBox="0 0 80 80" aria-hidden="true" style={{ width: 66, height: 66, display: "block" }}>
        <defs>
          <linearGradient id="benefit-teachers-gradient" x1="10" y1="13" x2="68" y2="66" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#e24dff" />
            <stop offset="1" stopColor="#039dff" />
          </linearGradient>
        </defs>
        <g {...common} stroke="url(#benefit-teachers-gradient)">
          <path d="M28 35.5a12 12 0 1 1 0-24 12 12 0 0 1 0 24Z" />
          <path d="M9.5 67c0-16 8.3-24.6 18.5-24.6S46.5 51 46.5 67" />
          <path d="M49.5 34.5a9.5 9.5 0 1 0-2.2-18.7" />
          <path d="M51 45.5c8.7 1.6 14.5 8.7 14.5 21.5" />
        </g>
      </svg>
    );
  }
  if (type === "globe") {
    return (
      <svg viewBox="0 0 80 80" aria-hidden="true" style={{ width: 66, height: 66, display: "block" }}>
        <defs>
          <linearGradient id="benefit-globe-gradient" x1="12" y1="10" x2="68" y2="69" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#ba49ff" />
            <stop offset="1" stopColor="#00c9ff" />
          </linearGradient>
        </defs>
        <g {...common} stroke="url(#benefit-globe-gradient)">
          <circle cx="40" cy="40" r="30" />
          <path d="M10 40h60M40 10c9 8 13.2 18 13.2 30S49 62 40 70M40 10c-9 8-13.2 18-13.2 30S31 62 40 70" />
        </g>
      </svg>
    );
  }
  if (type === "calendar") {
    return (
      <svg viewBox="0 0 80 80" aria-hidden="true" style={{ width: 66, height: 66, display: "block" }}>
        <defs>
          <linearGradient id="benefit-calendar-gradient" x1="12" y1="11" x2="70" y2="68" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#e24dff" />
            <stop offset="1" stopColor="#009dff" />
          </linearGradient>
        </defs>
        <g {...common} stroke="url(#benefit-calendar-gradient)">
          <path d="M18 19h36c5 0 8 3 8 8v30c0 5-3 8-8 8H18c-5 0-8-3-8-8V27c0-5 3-8 8-8ZM22 10v17M50 10v17M11 33h50" />
          <circle cx="56" cy="57" r="16.5" fill="#020617" stroke="none" />
          <circle cx="56" cy="57" r="14" />
          <path d="M56 49v9l7 4" />
        </g>
      </svg>
    );
  }
  return <HeroMusicNoteIcon size={74} compact />;
}

function LoggedInHero({ role, onFindTeachers, onPrimaryAction, ctaLabel }) {
  const handleTileAction = (event) => {
    event.preventDefault();
    onPrimaryAction?.();
  };

  const benefitItems = [
    { type: "teachers", href: "#find-teachers", onClick: onFindTeachers, text: <>Learn from<br />World-Class Teachers</> },
    { type: "globe", href: "#find-teachers", onClick: onFindTeachers, text: <>Connect<br />from Anywhere</> },
    { type: "calendar", href: "#find-teachers", onClick: onFindTeachers, text: <>Lessons<br />on Your Schedule</> },
    { type: "notes", href: "#find-teachers", onClick: onFindTeachers, text: <>All Instruments.<br />One Space.</> },
  ];
  return (
    <section className="logged-in-hero" style={{ position: "relative", width: "100%", maxWidth: "100%", aspectRatio: "1630 / 965", minHeight: 440, borderRadius: 22, overflow: "hidden", border: "1px solid rgba(148,163,184,0.14)", background: "#020617", marginBottom: 24, boxShadow: "0 22px 70px rgba(0,0,0,0.32)", boxSizing: "border-box" }}>
      <img src={loggedInHero} alt="" aria-hidden="true" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
      <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(2,6,23,0) 44%, rgba(2,6,23,0.17) 68%, rgba(2,6,23,0.34) 100%)" }} />
      <div className="logged-in-hero-logo" style={{ position: "absolute", left: "50%", top: "37%", transform: "translate(-50%, -50%)", zIndex: 2, width: "min(88%, 680px)", display: "flex", justifyContent: "center" }}>
        <button className="logged-in-hero-logo-control" type="button" onClick={onFindTeachers} aria-label="Go lower to find teachers">
          <MusicSpaceHeroLogo />
        </button>
      </div>
      <button onClick={onPrimaryAction} style={{ position: "absolute", right: 22, top: 20, zIndex: 3, padding: "9px 13px", borderRadius: 999, border: "1px solid rgba(255,255,255,0.18)", background: "rgba(2,6,23,0.48)", backdropFilter: "blur(10px)", color: "#f8fafc", fontSize: 12, fontWeight: 900, cursor: "pointer", boxShadow: "0 10px 24px rgba(0,0,0,0.28)" }}>{ctaLabel || (role === "teacher" ? "Go to dashboard" : "Book a lesson")}</button>
      <div className="logged-in-hero-menu" style={{ position: "absolute", left: "3.75%", right: "4.4%", bottom: "13.4%", zIndex: 3, display: "grid", gridTemplateColumns: "13.4% repeat(4, 1fr)", alignItems: "center", gap: "clamp(12px, 2vw, 28px)" }}>
        <a className="logged-in-hero-tile" href="#book-lesson" onClick={handleTileAction} aria-label="Book a lesson" style={{ position: "relative", aspectRatio: "1 / 1", width: "100%", maxWidth: 168, borderRadius: 22, border: "1.5px solid rgba(237,63,255,0.82)", backgroundImage: `url(${heroTileBackground})`, backgroundSize: "cover", backgroundPosition: "center", boxShadow: "inset 0 0 34px rgba(88,28,135,0.23), 0 0 20px rgba(6,182,212,0.12)", cursor: "pointer", overflow: "hidden", padding: 0, display: "block", textDecoration: "none" }}>
          <span className="logged-in-hero-tile-light logged-in-hero-tile-light-one" />
          <span className="logged-in-hero-tile-light logged-in-hero-tile-light-two" />
          <span className="logged-in-hero-tile-light logged-in-hero-tile-light-three" />
          <span className="logged-in-hero-tile-light logged-in-hero-tile-light-four" />
          <span className="logged-in-hero-tile-note" style={{ position: "absolute", left: "50%", top: "50%" }}>
            <HeroMusicNoteIcon size={86} compact />
          </span>
        </a>
        {benefitItems.map((item, index) => (
          <a className="logged-in-hero-benefit" key={item.type} href={item.href} onClick={item.onClick} style={{ minWidth: 0, display: "grid", gridTemplateColumns: index === 0 ? "auto 1fr" : "1px auto 1fr", alignItems: "center", columnGap: "clamp(13px, 1.8vw, 24px)", textDecoration: "none" }}>
            {index > 0 && <span className="logged-in-hero-divider" style={{ width: 1, height: 70, background: "linear-gradient(180deg, transparent, rgba(255,255,255,0.84) 18%, rgba(255,255,255,0.84) 82%, transparent)", justifySelf: "start" }} />}
            <span className="logged-in-hero-icon"><HeroBenefitIcon type={item.type} /></span>
            <span className="logged-in-hero-copy" style={{ color: "#f8fafc", fontSize: "clamp(13px, 1.26vw, 20px)", lineHeight: 1.5, fontWeight: 800, textAlign: "left", textShadow: "0 4px 14px rgba(0,0,0,0.7)", whiteSpace: "normal" }}>{item.text}</span>
          </a>
        ))}
      </div>
      <div style={{ position: "absolute", left: 22, top: 20, zIndex: 3, display: "flex", gap: 8, flexWrap: "wrap" }}>
        <span style={{ background: "rgba(2,6,23,0.46)", border: "1px solid rgba(255,255,255,0.16)", backdropFilter: "blur(10px)", borderRadius: 999, padding: "7px 11px", color: "#dbeafe", fontSize: 11, fontWeight: 900 }}>{role === "teacher" ? "Teacher portal" : "Student space"}</span>
      </div>
    </section>
  );
}

function toInitials(name) {
  return String(name || "")
    .trim()
    .split(/\s+/)
    .map((part) => part[0] || "")
    .join("")
    .slice(0, 2)
    .toUpperCase() || "MS";
}

function normalizeTeacher(teacher, fallback = {}) {
  return {
    id: teacher.id || fallback.id,
    name: teacher.name || fallback.name || "Music Space Teacher",
    av: teacher.av || fallback.av || toInitials(teacher.name || fallback.name),
    instruments: Array.isArray(teacher.instruments) && teacher.instruments.length ? teacher.instruments : fallback.instruments || [],
    level: teacher.level || fallback.level || "Beginner",
    lang: teacher.lang || fallback.lang || "Polish",
    rating: typeof teacher.rating === "number" ? teacher.rating : fallback.rating ?? 0,
    reviews: typeof teacher.reviews === "number" ? teacher.reviews : fallback.reviews ?? 0,
    price: typeof teacher.price === "number" ? teacher.price : fallback.price ?? 60,
    status: teacher.status || fallback.status || "offline",
    bio: teacher.bio || fallback.bio || "Teacher profile coming soon.",
    tags: Array.isArray(teacher.tags) ? teacher.tags : fallback.tags || [],
    photoDataUrl: teacher.photoDataUrl || fallback.photoDataUrl || "",
    cvDataUrl: teacher.cvDataUrl || fallback.cvDataUrl || "",
    cvFileName: teacher.cvFileName || fallback.cvFileName || "",
  };
}

function readStoredAppMode() {
  try {
    return window.localStorage.getItem(APP_MODE_KEY) === "live" ? "live" : "demo";
  } catch {
    return "demo";
  }
}

function storeAppMode(mode) {
  try {
    window.localStorage.setItem(APP_MODE_KEY, mode);
  } catch {
    // Restricted storage should not stop the UI mode switch.
  }
}

function readStoredLanguage() {
  try {
    const storedLanguage = window.localStorage.getItem(LANGUAGE_KEY);
    return LANGUAGES.some((language) => language.code === storedLanguage) ? storedLanguage : DEFAULT_LANGUAGE;
  } catch {
    return DEFAULT_LANGUAGE;
  }
}

function storeLanguage(languageCode) {
  try {
    window.localStorage.setItem(LANGUAGE_KEY, languageCode);
  } catch {
    // Restricted storage should not stop the language switch.
  }
}

function getTranslation(languageCode, key) {
  return TRANSLATIONS[languageCode]?.[key] || TRANSLATIONS[DEFAULT_LANGUAGE][key] || key;
}

function LanguageSelect({ language, onChange, label }) {
  return (
    <label style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
      <span style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap" }}>{label}</span>
      <select
        value={language}
        onChange={(event) => onChange(event.target.value)}
        title={label}
        aria-label={label}
        style={{
          appearance: "none",
          background: "#1e293b",
          border: "1px solid #334155",
          borderRadius: 10,
          color: "#f8fafc",
          cursor: "pointer",
          fontSize: 12,
          fontWeight: 800,
          minWidth: 82,
          padding: "7px 26px 7px 10px",
        }}
      >
        {LANGUAGES.map((languageOption) => (
          <option key={languageOption.code} value={languageOption.code}>
            {languageOption.flag} {languageOption.short} · {languageOption.label}
          </option>
        ))}
      </select>
      <span aria-hidden="true" style={{ position: "absolute", right: 10, color: "#94a3b8", fontSize: 10, pointerEvents: "none" }}>▼</span>
    </label>
  );
}

function TurnstileBox({ action, onVerify }) {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY || !containerRef.current) return undefined;
    let widgetId = null;
    let cancelled = false;

    const renderWidget = () => {
      if (cancelled || !window.turnstile || !containerRef.current) return;
      containerRef.current.innerHTML = "";
      widgetId = window.turnstile.render(containerRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        action,
        theme: "dark",
        callback: (token) => onVerify?.(token),
        "expired-callback": () => onVerify?.(""),
        "error-callback": () => onVerify?.(""),
      });
    };

    if (window.turnstile) {
      renderWidget();
    } else {
      const existing = document.querySelector('script[src*="challenges.cloudflare.com/turnstile"]');
      const script = existing || document.createElement("script");
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      script.onload = renderWidget;
      if (!existing) document.head.appendChild(script);
      else existing.addEventListener("load", renderWidget, { once: true });
    }

    return () => {
      cancelled = true;
      if (widgetId && window.turnstile) window.turnstile.remove(widgetId);
    };
  }, [action, onVerify]);

  if (!TURNSTILE_SITE_KEY) {
    return (
      <div style={{ border: "1px solid #334155", borderRadius: 10, padding: "12px 13px", background: "rgba(15,23,42,0.72)", color: "#cbd5e1", fontSize: 12, lineHeight: 1.45, marginBottom: 12, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <span style={{ display: "flex", alignItems: "center", gap: 9 }}>
          <span style={{ width: 18, height: 18, borderRadius: 5, border: "1px solid #475569", background: "#020617", display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#22c55e", fontSize: 12, fontWeight: 900 }}>✓</span>
          Security check verified
        </span>
        <span style={{ color: "#64748b", fontSize: 11, fontWeight: 800 }}>Cloudflare</span>
      </div>
    );
  }

  return <div ref={containerRef} style={{ minHeight: 65, marginBottom: 12 }} />;
}

function ModeSwitch({ mode, onChange, labels = { demo: "Demo", live: "Live" } }) {
  return (
    <div style={{ display: "flex", background: "#1e293b", border: "1px solid #334155", borderRadius: 10, padding: 3 }}>
      {[["demo", labels.demo], ["live", labels.live]].map(([value, label]) => (
        <button
          key={value}
          type="button"
          onClick={() => onChange(value)}
          style={{
            padding: "6px 10px",
            border: "none",
            borderRadius: 7,
            background: mode === value ? "#0f172a" : "transparent",
            color: mode === value ? "#f8fafc" : "#94a3b8",
            fontSize: 12,
            fontWeight: 800,
            cursor: "pointer",
          }}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : "");
    reader.onerror = () => reject(new Error(`Could not read ${file.name}.`));
    reader.readAsDataURL(file);
  });
}

function getTeacherFrame(teacher) {
  const matchedInstrument = (teacher.instruments || []).find((instrument) => INSTRUMENT_FRAME_MAP[instrument]);
  return INSTRUMENT_FRAME_MAP[matchedInstrument] || HOME_GALLERY_FRAMES[0];
}

function getInstrumentFrame(instrument) {
  return INSTRUMENT_FRAME_MAP[instrument] || HOME_GALLERY_FRAMES[0];
}

function TeacherCoverVisual({ teacher, alt }) {
  if (teacher.photoDataUrl) {
    return <img src={teacher.photoDataUrl} alt={alt} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />;
  }
  return <CollageFrameImage frame={getTeacherFrame(teacher)} alt={alt} />;
}

function InstrumentFrameVisual({ instrument, alt }) {
  return <CollageFrameImage frame={getInstrumentFrame(instrument)} alt={alt || instrument} />;
}

function getSessionMaterials(teacher) {
  const primaryInstrument = teacher.instruments?.[0] || "Music";
  const materials = [
    {
      id: `${teacher.id}-pdf`,
      type: "pdf",
      title: `${primaryInstrument} lesson notes`,
      subtitle: "PDF shared by the teacher during the lesson",
      href: "/materials/c-major-exercise.pdf",
    },
    {
      id: `${teacher.id}-image`,
      type: "image",
      title: "Reference image",
      subtitle: "Visual material for posture, setup, or phrase ideas",
      href: "/materials/teacher-reference.png",
      preview: "/materials/teacher-reference.png",
    },
  ];
  if (teacher.cvDataUrl) {
    materials.unshift({
      id: `${teacher.id}-cv`,
      type: "pdf",
      title: teacher.cvFileName || "Teacher CV",
      subtitle: "Professional background shared by the teacher",
      href: teacher.cvDataUrl,
    });
  }
  return materials;
}

// ─── Ambient Music Hook ───────────────────────────────────────────────────────
// Plays the bundled background track with the existing music controls.
function useAmbientMusic() {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [volume, setVolume] = useState(0.35);

  const toggle = async () => {
    if (!audioRef.current) {
      const audio = new Audio(backgroundTrack);
      audio.loop = true;
      audio.preload = "auto";
      audio.volume = volume;
      audioRef.current = audio;
    }

    if (!playing) {
      try {
        await audioRef.current.play();
        setPlaying(true);
      } catch (error) {
        console.error("Unable to play background track:", error);
      }
    } else {
      audioRef.current.pause();
      setPlaying(false);
    }
  };

  const changeVolume = (v) => {
    setVolume(v);
    if (audioRef.current) {
      audioRef.current.volume = v;
    }
  };

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current = null;
      }
    };
  }, []);

  return { playing, toggle, volume, changeVolume };
}

// ─── Audio Button ─────────────────────────────────────────────────────────────
function AudioButton({ playing, onToggle, volume, onVolume }) {
  const [showVol, setShowVol] = useState(false);
  return (
    <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 200, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8 }}>
      {showVol && (
        <div style={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 12, padding: "10px 14px", display: "flex", alignItems: "center", gap: 10, animation: "slideUp 0.2s ease" }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M19.07 4.93a10 10 0 010 14.14M15.54 8.46a5 5 0 010 7.07"/></svg>
          <input type="range" min="0" max="1" step="0.05" value={volume} onChange={e => onVolume(parseFloat(e.target.value))}
            style={{ width: 90, accentColor: "#7c3aed", cursor: "pointer" }} />
          <span style={{ fontSize: 11, color: "#475569", minWidth: 28 }}>{Math.round(volume * 100)}%</span>
        </div>
      )}
      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={() => setShowVol(!showVol)}
          style={{ width: 40, height: 40, borderRadius: "50%", background: "#0f172a", border: "1px solid #1e293b", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.2s" }}
          title="Volume">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M15.54 8.46a5 5 0 010 7.07"/></svg>
        </button>
        <button onClick={onToggle}
          style={{ width: 52, height: 52, borderRadius: "50%", background: playing ? "#7c3aed" : "#0f172a", border: `2px solid ${playing ? "#7c3aed" : "#1e293b"}`, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.3s", boxShadow: playing ? "0 0 20px rgba(124,58,237,0.4)" : "none", position: "relative" }}
          title={playing ? "Pause ambient music" : "Play ambient music"}>
          {playing && (
            <span style={{ position: "absolute", inset: -4, borderRadius: "50%", border: "2px solid rgba(124,58,237,0.3)", animation: "ripple 2s linear infinite" }} />
          )}
          {playing ? (
            /* Pause icon */
            <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
              <rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/>
            </svg>
          ) : (
            /* Music note icon */
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2">
              <path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/>
            </svg>
          )}
        </button>
      </div>
      <div style={{ fontSize: 10, color: "#334155", textAlign: "center", letterSpacing: 0.5 }}>
        {playing ? "♪ AMBIENT" : "MUSIC"}
      </div>
    </div>
  );
}

// ─── Photo Gallery ────────────────────────────────────────────────────────────
function CollageFrameImage({ frame, alt }) {
  return (
    <div style={{ position: "absolute", inset: 0, overflow: "hidden" }}>
      <img
        src={frame.src}
        alt={alt}
        style={{
          position: "absolute",
          width: "200%",
          height: "200%",
          maxWidth: "none",
          left: `${frame.cropX * -100}%`,
          top: `${frame.cropY * -100}%`,
          objectFit: "cover",
          userSelect: "none",
          pointerEvents: "none",
        }}
      />
    </div>
  );
}

function SplitPhotoTile({ frame, height = 220 }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: "relative",
        borderRadius: 20,
        overflow: "hidden",
        minHeight: height,
        aspectRatio: COLLAGE_ASPECT_RATIO,
        cursor: "pointer",
        transform: hovered ? "translateY(-4px) scale(1.01)" : "translateY(0) scale(1)",
        transition: "transform 0.35s ease, box-shadow 0.35s ease",
        boxShadow: hovered ? "0 22px 50px rgba(15,23,42,0.42)" : "0 12px 30px rgba(2,6,23,0.22)",
      }}
    >
      <CollageFrameImage frame={frame} alt={frame.label} />
      <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(15,23,42,0.08) 5%, rgba(15,23,42,0.28) 48%, rgba(2,6,23,0.78) 100%)" }} />
      <div style={{ position: "absolute", inset: 0, border: "1px solid rgba(255,255,255,0.1)", borderRadius: 20 }} />
      <div style={{ position: "absolute", left: 18, right: 18, bottom: 16 }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: "#f8fafc", marginBottom: 4 }}>{frame.label}</div>
        <div style={{ fontSize: 12, lineHeight: 1.45, color: "rgba(226,232,240,0.8)", maxWidth: 220 }}>{frame.desc}</div>
      </div>
    </div>
  );
}

function PhotoGallery() {
  return (
    <div className="photo-gallery-section" style={{ maxWidth: 1100, margin: "0 auto", padding: "56px 20px" }}>
      <div style={{ textAlign: "center", marginBottom: 32 }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "6px 14px", borderRadius: 999, background: "rgba(124,58,237,0.1)", border: "1px solid rgba(167,139,250,0.18)", color: "#c4b5fd", fontSize: 12, fontWeight: 700, letterSpacing: 0.4, marginBottom: 16 }}>
          Split-frame gallery
        </div>
        <h2 style={{ fontSize: 30, fontWeight: 800, color: "#f1f5f9", marginBottom: 8 }}>Moments of Music</h2>
      </div>

      <div className="photo-gallery-grid" style={{ display: "grid", gap: 18, marginBottom: 22 }}>
        {HOME_GALLERY_FRAMES.map((frame) => (
          <SplitPhotoTile key={frame.id} frame={frame} height={230} />
        ))}
      </div>

      <div className="photo-gallery-stats" style={{ display: "grid", gap: 16 }}>
        {[["🎓","500+","Students taught"],["⭐","4.9","Average rating"],["🌍",String(LANGUAGES.length),"Languages available"],["⚡","Live","Instant connect"]].map(([emoji,val,label]) => (
          <div key={label} style={{ background: "linear-gradient(180deg, rgba(15,23,42,0.92), rgba(8,15,30,0.92))", border: "1px solid rgba(148,163,184,0.14)", borderRadius: 16, padding: "20px 18px", display: "flex", alignItems: "center", gap: 14 }}>
            <span style={{ fontSize: 28 }}>{emoji}</span>
            <div>
              <div style={{ fontSize: 22, fontWeight: 800, color: "#f1f5f9" }}>{val}</div>
              <div style={{ fontSize: 12, color: "#64748b" }}>{label}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── 3D Constellation Canvas ──────────────────────────────────────────────────
function ConstellationCanvas() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animId;
    let constellations = [];
    const W = () => canvas.offsetWidth;
    const H = () => canvas.offsetHeight;
    function resize() {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = canvas.offsetWidth * dpr;
      canvas.height = canvas.offsetHeight * dpr;
      ctx.scale(dpr, dpr);
    }
    function rotateY(p, a) { const c=Math.cos(a),s=Math.sin(a); return {x:p.x*c-p.z*s,y:p.y,z:p.x*s+p.z*c}; }
    function rotateX(p, a) { const c=Math.cos(a),s=Math.sin(a); return {x:p.x,y:p.y*c-p.z*s,z:p.y*s+p.z*c}; }
    function project(p, cx, cy, fov) { const z=p.z+fov, scale=fov/z; return {x:cx+p.x*scale,y:cy+p.y*scale,s:scale}; }
    function pt(x,y,z){return{x,y,z};}
    function makeShape(cx, cy) {
      const types=["triangle","diamond","pentagon","line3","cross"];
      const type=types[Math.floor(Math.random()*types.length)];
      let pts=[];
      if(type==="triangle"){const r=55+Math.random()*45;pts=[0,1,2].map(i=>{const a=(i/3)*Math.PI*2-Math.PI/2;return pt(Math.cos(a)*r,Math.sin(a)*r,(Math.random()-.5)*30);});}
      else if(type==="diamond"){const r=50+Math.random()*30;pts=[0,1,2,3].map(i=>{const a=(i/4)*Math.PI*2;return pt(Math.cos(a)*r,Math.sin(a)*r,(Math.random()-.5)*25);});}
      else if(type==="pentagon"){const r=45+Math.random()*35;pts=[0,1,2,3,4].map(i=>{const a=(i/5)*Math.PI*2-Math.PI/2;return pt(Math.cos(a)*r,Math.sin(a)*r,(Math.random()-.5)*35);});}
      else if(type==="line3"){pts=[0,1,2].map(i=>pt((i-1)*50,(Math.random()-.5)*30,(Math.random()-.5)*20));}
      else{const r=40;pts=[pt(-r,0,0),pt(r,0,0),pt(0,-r,0),pt(0,r,15)];}
      const edges=[];
      for(let i=0;i<pts.length;i++){edges.push([i,(i+1)%pts.length]);if(pts.length>3&&Math.random()>.55&&i<pts.length-2)edges.push([i,i+2]);}
      return{pts,edges,cx,cy,ry:Math.random()*Math.PI*2,rx:Math.random()*Math.PI*2,speedY:(Math.random()-.5)*.007,speedX:(Math.random()-.5)*.004,driftX:(Math.random()-.5)*.25,driftY:(Math.random()-.5)*.18,hue:180+Math.random()*120,opacity:.45+Math.random()*.45};
    }
    function init(){constellations=[];const count=Math.floor(W()*H()/52000)+8;for(let i=0;i<count;i++)constellations.push(makeShape(Math.random()*W(),Math.random()*H()));}
    const stars=Array.from({length:90},()=>({x:Math.random(),y:Math.random(),r:.5+Math.random()*1.5,flicker:Math.random()*Math.PI*2,speed:.018+Math.random()*.035,hue:180+Math.random()*120}));
    function draw(){
      ctx.clearRect(0,0,W(),H());
      stars.forEach(s=>{s.flicker+=s.speed;const alpha=.15+Math.sin(s.flicker)*.28;ctx.beginPath();ctx.arc(s.x*W(),s.y*H(),s.r,0,Math.PI*2);ctx.fillStyle=`hsla(${s.hue},80%,80%,${Math.max(0,alpha)})`;ctx.fill();});
      constellations.forEach(c=>{
        c.ry+=c.speedY;c.rx+=c.speedX;c.cx+=c.driftX;c.cy+=c.driftY;
        if(c.cx<-120)c.cx=W()+120;if(c.cx>W()+120)c.cx=-120;if(c.cy<-120)c.cy=H()+120;if(c.cy>H()+120)c.cy=-120;
        const fov=280;
        const proj=c.pts.map(p=>{let r=rotateY(p,c.ry);r=rotateX(r,c.rx);return project(r,c.cx,c.cy,fov);});
        c.edges.forEach(([a,b])=>{const pa=proj[a],pb=proj[b],avg=(pa.s+pb.s)*.5;ctx.beginPath();ctx.moveTo(pa.x,pa.y);ctx.lineTo(pb.x,pb.y);ctx.strokeStyle=`hsla(${c.hue},75%,65%,${Math.min(1,avg*.95)*c.opacity*.6})`;ctx.lineWidth=Math.max(.3,avg*.75);ctx.stroke();});
        proj.forEach((p,i)=>{const alpha=Math.min(1,p.s*.95)*c.opacity;const r=Math.max(.8,p.s*1.8);ctx.beginPath();ctx.arc(p.x,p.y,r,0,Math.PI*2);ctx.fillStyle=`hsla(${c.hue},85%,75%,${alpha})`;ctx.fill();if(i===0){ctx.beginPath();ctx.arc(p.x,p.y,r*3.5,0,Math.PI*2);ctx.fillStyle=`hsla(${c.hue},85%,75%,${alpha*.07})`;ctx.fill();}});
      });
      animId=requestAnimationFrame(draw);
    }
    resize();init();draw();
    const onResize=()=>{resize();init();};
    window.addEventListener("resize",onResize);
    return()=>{cancelAnimationFrame(animId);window.removeEventListener("resize",onResize);};
  },[]);
  return <canvas ref={canvasRef} style={{position:"absolute",inset:0,width:"100%",height:"100%",pointerEvents:"none"}}/>;
}

function MusicCosmosBackdrop({ playing, volume }) {
  const energy = playing ? 0.5 + volume * 0.8 : 0.18;

  return (
    <>
      <div style={{ position: "absolute", inset: 0, background: "radial-gradient(circle at 20% 20%, rgba(59,130,246,0.14), transparent 36%), radial-gradient(circle at 78% 18%, rgba(168,85,247,0.16), transparent 34%), radial-gradient(circle at 50% 72%, rgba(34,197,94,0.08), transparent 30%), linear-gradient(180deg, rgba(7,10,21,0.98), rgba(9,13,27,0.94))" }} />
      <ConstellationCanvas />
      <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(11,14,22,0.08) 0%, rgba(11,14,22,0.48) 55%, rgba(11,14,22,0.86) 100%)" }} />

      {Array.from({ length: 22 }).map((_, index) => (
        <span
          key={`star-${index}`}
          style={{
            position: "absolute",
            left: `${(index * 17) % 100}%`,
            top: `${(index * 29) % 100}%`,
            width: index % 3 === 0 ? 3 : 2,
            height: index % 3 === 0 ? 3 : 2,
            borderRadius: "50%",
            background: index % 2 === 0 ? "rgba(191,219,254,0.85)" : "rgba(221,214,254,0.8)",
            boxShadow: "0 0 16px rgba(191,219,254,0.5)",
            animation: `twinkle ${3 + (index % 5)}s ease-in-out ${index * 0.12}s infinite`,
            opacity: 0.35 + energy * 0.8,
            pointerEvents: "none",
          }}
        />
      ))}

      {FLOATING_NOTES.map((note, index) => (
        <div
          key={`${note}-${index}`}
          style={{
            position: "absolute",
            left: `${8 + index * 13}%`,
            top: `${12 + (index % 4) * 18}%`,
            fontSize: `${22 + (index % 3) * 10}px`,
            color: index % 2 === 0 ? "rgba(196,181,253,0.32)" : "rgba(125,211,252,0.28)",
            textShadow: "0 0 18px rgba(99,102,241,0.18)",
            animation: `noteDrift ${8 + index * 0.85}s ease-in-out ${index * 0.35}s infinite`,
            transform: `scale(${playing ? 1 + volume * 0.16 : 0.92}) rotate(${index % 2 === 0 ? -8 : 10}deg)`,
            opacity: playing ? 0.24 + volume * 0.32 : 0.14,
            pointerEvents: "none",
          }}
        >
          {note}
        </div>
      ))}
    </>
  );
}

function CosmosSection({ children, playing, volume }) {
  return (
    <div style={{ position: "relative", overflow: "hidden", borderTop: "1px solid rgba(30,41,59,0.8)" }}>
      <MusicCosmosBackdrop playing={playing} volume={volume} />
      <div style={{ position: "relative", zIndex: 1 }}>{children}</div>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function StatusBadge({status}){
  const colors={available:"#22c55e",busy:"#f59e0b",offline:"#9ca3af"};
  const labels={available:"Available now",busy:"In session",offline:"Offline"};
  return(<span style={{display:"inline-flex",alignItems:"center",gap:5}}><span style={{width:8,height:8,borderRadius:"50%",background:colors[status],display:"inline-block",animation:status==="available"?"pulse 2s infinite":"none"}}/><span style={{fontSize:11,color:colors[status],fontWeight:600}}>{labels[status]}</span></span>);
}
function Stars({r}){return(<span style={{display:"inline-flex",gap:1}}>{[1,2,3,4,5].map(i=><svg key={i} width="11" height="11" viewBox="0 0 12 12"><polygon points="6,1 7.5,4.5 11,4.5 8.5,7 9.5,10.5 6,8.5 2.5,10.5 3.5,7 1,4.5 4.5,4.5" fill={i<=Math.round(r)?"#f59e0b":"#1e293b"}/></svg>)}</span>);}
function bookingStatusLabel(booking) {
  if (booking.status === "searching_teacher") return "Finding teacher";
  if (booking.status === "payment_pending") return "Payment pending";
  if (booking.status === "paid_confirmed") return "Paid";
  if (booking.status === "trial_confirmed") return "Trial confirmed";
  if (booking.status === "refunded") return "Refunded";
  if (booking.status === "cancelled") return "Cancelled";
  if (booking.status === "teacher_withdrew") return "Teacher withdrew";
  return booking.teacher ? "Teacher assigned" : "Booked";
}
function bookingPaymentLabel(booking) {
  if (booking.refund?.status || booking.payment?.status === "refunded") return "Refund in progress";
  if (booking.isTrial || booking.payment?.status === "trial") return "Free trial";
  if (booking.payment?.status === "paid") return "Paid";
  if (booking.payment?.status === "pending") return "Awaiting payment";
  if (booking.payment?.status === "cancelled") return "Payment cancelled";
  return "Not paid";
}
function canCancelBooking(booking) {
  return !["cancelled", "teacher_withdrew", "refunded"].includes(booking.status);
}
function LessonBookingCard({ booking, role, onCancel, onClaim, onRefund }) {
  const closed = ["cancelled", "teacher_withdrew", "refunded"].includes(booking.status);
  const paid = booking.status === "paid_confirmed" || booking.status === "trial_confirmed" || booking.payment?.status === "paid";
  const statusColor = closed ? "#fca5a5" : paid ? "#86efac" : "#93c5fd";
  const statusBg = closed ? "rgba(239,68,68,0.1)" : paid ? "rgba(34,197,94,0.1)" : "rgba(59,130,246,0.12)";
  return (
    <div style={{ background:"rgba(15,23,42,0.72)", border:"1px solid rgba(148,163,184,0.12)", borderRadius:16, padding:16, marginBottom:10 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:12, flexWrap:"wrap", marginBottom:10 }}>
        <div>
          <div style={{ fontWeight:800, fontSize:15, color:"#f1f5f9", marginBottom:4 }}>{booking.isTrial ? "Free trial" : booking.instrument} {booking.isTrial ? `· ${booking.instrument}` : "lesson"}</div>
          <div style={{ fontSize:12, color:"#94a3b8" }}>{booking.date} at {booking.time} · {booking.duration}</div>
          <div style={{ fontSize:12, color:"#64748b", marginTop:4 }}>
            {booking.teacher ? `Teacher: ${booking.teacher.name}` : "Teacher: waiting for match"}
            {role === "teacher" && booking.student?.name ? ` · Student: ${booking.student.name}` : ""}
          </div>
        </div>
        <div style={{ display:"flex", gap:7, alignItems:"center", flexWrap:"wrap" }}>
          <span style={{ background:statusBg, color:statusColor, padding:"4px 9px", borderRadius:20, fontSize:11, fontWeight:800 }}>{bookingStatusLabel(booking)}</span>
          <span style={{ background:"rgba(15,23,42,0.9)", color:"#cbd5e1", border:"1px solid rgba(148,163,184,0.12)", padding:"4px 9px", borderRadius:20, fontSize:11, fontWeight:800 }}>{bookingPaymentLabel(booking)}</span>
        </div>
      </div>
      {booking.notes && <div style={{ fontSize:12, color:"#94a3b8", lineHeight:1.5, marginBottom:10 }}>{booking.notes}</div>}
      {booking.emailNotice && <div style={{ fontSize:12, color:"#60a5fa", lineHeight:1.5, marginBottom:10 }}>{booking.emailNotice}</div>}
      {closed && <div style={{ fontSize:12, color:"#fca5a5", lineHeight:1.5, marginBottom:10 }}>Lesson was cancelled. {booking.payment?.status === "refunded" || booking.refund?.status ? "The money will return to the student through Stripe." : "No paid refund is needed yet."}</div>}
      <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
        {role === "teacher" && booking.status === "searching_teacher" && <button onClick={() => onClaim?.(booking)} style={{ padding:"9px 12px", background:"#22c55e", color:"white", border:"none", borderRadius:9, fontWeight:800, fontSize:12, cursor:"pointer" }}>I can take it</button>}
        {role === "student" && !booking.isTrial && booking.payment?.checkoutUrl && booking.payment?.status !== "paid" && booking.payment?.status !== "refunded" && booking.payment?.status !== "cancelled" && <a href={booking.payment.checkoutUrl} style={{ padding:"8px 11px", background:"#7c3aed", color:"white", borderRadius:9, fontSize:12, fontWeight:800, textDecoration:"none" }}>Pay now</a>}
        {canCancelBooking(booking) && <button onClick={() => onCancel?.(booking)} style={{ padding:"8px 10px", background:"transparent", color:"#fca5a5", border:"1px solid rgba(239,68,68,0.24)", borderRadius:9, fontWeight:800, fontSize:12, cursor:"pointer" }}>{role === "teacher" ? "Withdraw" : "Cancel"}</button>}
        {role === "teacher" && booking.status === "paid_confirmed" && <button onClick={() => onRefund?.(booking)} style={{ padding:"8px 10px", background:"rgba(239,68,68,0.08)", color:"#fca5a5", border:"1px solid rgba(239,68,68,0.22)", borderRadius:9, fontWeight:800, fontSize:12, cursor:"pointer" }}>Refund</button>}
      </div>
    </div>
  );
}

// ─── Teacher Card ─────────────────────────────────────────────────────────────
function TeacherCard({t,onConnect,onBook,onShowCv}){
  const[hov,setHov]=useState(false);
  const teacherFrame = getTeacherFrame(t);
  return(
    <div onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)}
      style={{background:"#0f172a",border:`1px solid ${hov?"#7c3aed55":"#1e293b"}`,borderRadius:16,padding:18,transition:"all 0.2s",transform:hov?"translateY(-3px)":"none",boxShadow:hov?"0 8px 30px rgba(124,58,237,0.15)":"none",position:"relative",overflow:"hidden"}}>
      {t.status==="available"&&<div style={{position:"absolute",top:0,left:0,right:0,height:2,background:"linear-gradient(90deg,#22c55e,#16a34a)"}}/>}
      <div
        style={{
          aspectRatio: COLLAGE_ASPECT_RATIO,
          margin: "-18px -18px 16px",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <TeacherCoverVisual teacher={t} alt={t.name} />
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(15,23,42,0.06) 0%, rgba(15,23,42,0.2) 45%, rgba(15,23,42,0.85) 100%)" }} />
        <div style={{ position: "absolute", left: 14, bottom: 12, display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 10px", borderRadius: 999, background: "rgba(15,23,42,0.58)", border: "1px solid rgba(255,255,255,0.12)", color: "#e2e8f0", fontSize: 11, fontWeight: 700 }}>
          {teacherFrame.label}
        </div>
      </div>
      <div style={{display:"flex",gap:12,marginBottom:12}}>
        <div style={{width:48,height:48,borderRadius:"50%",background:t.status==="available"?"#052e16":t.status==="busy"?"#1c1400":"#1e293b",display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:800,color:t.status==="available"?"#22c55e":t.status==="busy"?"#f59e0b":"#64748b",flexShrink:0,position:"relative"}}>
          {t.av}<span style={{position:"absolute",bottom:1,right:1,width:11,height:11,borderRadius:"50%",background:t.status==="available"?"#22c55e":t.status==="busy"?"#f59e0b":"#475569",border:"2px solid #0f172a"}}/>
        </div>
        <div style={{flex:1,minWidth:0}}>
          <button onClick={() => onShowCv(t)} style={{fontWeight:700,fontSize:14,color:"#f1f5f9",background:"none",border:"none",padding:0,cursor:"pointer",textAlign:"left"}}>{t.name}</button>
          <div style={{fontSize:11,color:"#475569",marginBottom:3}}>{t.instruments.join(" · ")} · {t.lang}</div>
          <StatusBadge status={t.status}/>
        </div>
        <div style={{textAlign:"right",flexShrink:0}}><div style={{fontSize:17,fontWeight:800,color:"#f1f5f9"}}>${t.price}</div><div style={{fontSize:10,color:"#475569"}}>/lesson</div></div>
      </div>
      <p style={{fontSize:12,color:"#64748b",lineHeight:1.5,marginBottom:10,display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden"}}>{t.bio}</p>
      <div style={{display:"flex",flexWrap:"wrap",gap:4,marginBottom:10}}>{t.tags.map(tag=><span key={tag} style={{background:"#1e293b",color:"#94a3b8",fontSize:10,padding:"2px 8px",borderRadius:20,fontWeight:500}}>{tag}</span>)}</div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}><span style={{display:"flex",alignItems:"center",gap:4}}><Stars r={t.rating}/><span style={{fontSize:11,color:"#475569"}}>{t.rating} ({t.reviews})</span></span><span style={{fontSize:11,color:"#475569"}}>{t.level}</span></div>
      <div style={{display:"flex",gap:7}}>
        {t.status==="available"?<button onClick={()=>onConnect(t)} style={{flex:1,padding:"9px 12px",background:"#22c55e",color:"white",border:"none",borderRadius:9,fontSize:12,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:5}}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.63A2 2 0 012 .18h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 7.91a16 16 0 006.08 6.08l1.29-1.29a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 14.92z"/></svg>Connect Now</button>:<button disabled style={{flex:1,padding:"9px 12px",background:"#1e293b",color:"#475569",border:"none",borderRadius:9,fontSize:12,fontWeight:600,cursor:"not-allowed"}}>{t.status==="busy"?"🟡 In Session":"⚫ Offline"}</button>}
        <button onClick={()=>onBook(t)} style={{flex:1,padding:"9px 12px",background:"transparent",color:"#94a3b8",border:"1px solid #1e293b",borderRadius:9,fontSize:12,fontWeight:600,cursor:"pointer"}} onMouseEnter={e=>{e.currentTarget.style.borderColor="#334155";e.currentTarget.style.color="#e2e8f0";}} onMouseLeave={e=>{e.currentTarget.style.borderColor="#1e293b";e.currentTarget.style.color="#94a3b8";}}>Book Lesson</button>
      </div>
    </div>
  );
}

function TeacherCvModal({ teacher, onClose }) {
  if (!teacher) return null;
  const teacherFrame = getTeacherFrame(teacher);
  const tags = teacher.tags || [];

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(2,6,23,0.78)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1500, padding: 20 }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ width: "100%", maxWidth: 760, maxHeight: "88vh", overflowY: "auto", background: "#0f172a", border: "1px solid #1e293b", borderRadius: 24, boxShadow: "0 30px 80px rgba(2,6,23,0.45)" }}>
        <div style={{ position: "relative", aspectRatio: "16 / 8", overflow: "hidden" }}>
          <TeacherCoverVisual teacher={teacher} alt={teacher.name} />
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(15,23,42,0.04) 0%, rgba(15,23,42,0.32) 38%, rgba(15,23,42,0.92) 100%)" }} />
          <button onClick={onClose} style={{ position: "absolute", top: 16, right: 16, width: 36, height: 36, borderRadius: "50%", border: "1px solid rgba(255,255,255,0.15)", background: "rgba(15,23,42,0.68)", color: "#e2e8f0", fontSize: 20, cursor: "pointer" }}>×</button>
          <div style={{ position: "absolute", left: 22, right: 22, bottom: 20, display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 16 }}>
            <div>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "5px 11px", borderRadius: 999, background: "rgba(15,23,42,0.58)", border: "1px solid rgba(255,255,255,0.12)", color: "#cbd5e1", fontSize: 11, fontWeight: 700, marginBottom: 12 }}>
                {teacherFrame.label}
              </div>
              <h2 style={{ fontSize: 28, fontWeight: 800, color: "#f8fafc", marginBottom: 6 }}>{teacher.name}</h2>
              <div style={{ fontSize: 13, color: "#cbd5e1" }}>{teacher.instruments.join(" · ")} · {teacher.lang}</div>
            </div>
            <div style={{ width: 72, height: 72, borderRadius: "50%", background: "rgba(15,23,42,0.8)", border: "1px solid rgba(255,255,255,0.16)", display: "flex", alignItems: "center", justifyContent: "center", color: "#f8fafc", fontSize: 22, fontWeight: 800, flexShrink: 0 }}>
              {teacher.av}
            </div>
          </div>
        </div>

        <div style={{ padding: 24 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 12, marginBottom: 22 }}>
            {[["Level", teacher.level], ["Rate", `$${teacher.price} / lesson`], ["Language", teacher.lang], ["Reviews", `${teacher.reviews} reviews`]].map(([label, value]) => (
              <div key={label} style={{ background: "rgba(15,23,42,0.72)", border: "1px solid rgba(148,163,184,0.12)", borderRadius: 16, padding: "14px 16px" }}>
                <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.8, color: "#64748b", marginBottom: 5 }}>{label}</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: "#f8fafc" }}>{value}</div>
              </div>
            ))}
          </div>

          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 1, color: "#64748b", marginBottom: 8 }}>Profile</div>
            <p style={{ fontSize: 14, lineHeight: 1.7, color: "#cbd5e1" }}>{teacher.bio}</p>
          </div>

          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 1, color: "#64748b", marginBottom: 8 }}>Specialties</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {tags.length > 0 ? tags.map((tag) => (
                <span key={tag} style={{ background: "#1e293b", color: "#cbd5e1", fontSize: 12, padding: "6px 10px", borderRadius: 999 }}>{tag}</span>
              )) : <span style={{ color: "#64748b", fontSize: 13 }}>Teacher has not added specialties yet.</span>}
            </div>
          </div>

          {teacher.cvDataUrl && (
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 1, color: "#64748b", marginBottom: 8 }}>CV</div>
              <a href={teacher.cvDataUrl} download={teacher.cvFileName || `${teacher.name}-cv`} style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 14px", borderRadius: 12, background: "rgba(59,130,246,0.12)", border: "1px solid rgba(96,165,250,0.22)", color: "#bfdbfe", fontSize: 13, fontWeight: 700, textDecoration: "none" }}>
                Open CV
              </a>
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Stars r={teacher.rating} />
              <span style={{ fontSize: 13, color: "#94a3b8" }}>{teacher.rating} average rating</span>
            </div>
            <div style={{ fontSize: 12, color: "#64748b" }}>{teacher.status === "available" ? "Available for instant lesson" : teacher.status === "busy" ? "Currently in session" : "Offline right now"}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function UserProfileModal({ user, role, onClose }) {
  if (!user) return null;
  const profileRows = [
    ["Name", user.name || "Music Space User"],
    ["Email", user.email || "Not provided"],
    ["Role", role || "student"],
  ];

  return (
    <div id="user-profile" style={{ position: "fixed", inset: 0, background: "rgba(2,6,23,0.78)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1500, padding: 20 }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ width: "100%", maxWidth: 520, background: "#0f172a", border: "1px solid #1e293b", borderRadius: 24, boxShadow: "0 30px 80px rgba(2,6,23,0.45)", overflow: "hidden" }}>
        <div style={{ padding: 24, borderBottom: "1px solid #1e293b", display: "flex", justifyContent: "space-between", gap: 16, alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{ width: 58, height: 58, borderRadius: "50%", background: role === "teacher" ? "#1c1400" : "#2e1065", display: "flex", alignItems: "center", justifyContent: "center", color: role === "teacher" ? "#f59e0b" : "#c4b5fd", fontSize: 20, fontWeight: 900 }}>
              {(user.name || role || "MS").slice(0, 1).toUpperCase()}
            </div>
            <div>
              <h2 style={{ fontSize: 20, color: "#f8fafc", fontWeight: 850, marginBottom: 4 }}>User Profile</h2>
              <div style={{ fontSize: 13, color: "#94a3b8" }}>Music Space account details</div>
            </div>
          </div>
          <button onClick={onClose} style={{ width: 36, height: 36, borderRadius: "50%", border: "1px solid rgba(255,255,255,0.15)", background: "rgba(15,23,42,0.68)", color: "#e2e8f0", fontSize: 20, cursor: "pointer" }}>×</button>
        </div>
        <div style={{ padding: 24, display: "grid", gap: 10 }}>
          {profileRows.map(([label, value]) => (
            <div key={label} style={{ background: "rgba(15,23,42,0.72)", border: "1px solid rgba(148,163,184,0.12)", borderRadius: 14, padding: "13px 15px" }}>
              <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.8, color: "#64748b", marginBottom: 5 }}>{label}</div>
              <div style={{ fontSize: 15, fontWeight: 750, color: "#f8fafc" }}>{value}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Connect Flow ─────────────────────────────────────────────────────────────
function ConnectFlow({ teacher, token, onClose, onAccepted }) {
  const [phase, setPhase] = useState("creating");
  const [cd, setCd] = useState(90);
  const [request, setRequest] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!teacher || !token) return undefined;

    let cancelled = false;
    let source;

    const createRequest = async () => {
      try {
        const payload = await apiRequest("/api/video/requests", {
          method: "POST",
          token,
          body: {
            teacherId: String(teacher.id),
            teacherName: teacher.name,
            topic: `${teacher.instruments?.[0] || "Music"} instant lesson`,
          },
        });

        if (cancelled) return;
        setRequest(payload.request);
        setPhase("waiting");

        source = new EventSource(getSseUrl(`/api/video/requests/${payload.request.id}/events`, { token }));
        source.addEventListener("accepted", (event) => {
          const accepted = JSON.parse(event.data);
          setRequest(accepted);
          setPhase("accepted");
        });
        source.addEventListener("declined", (event) => {
          setRequest(JSON.parse(event.data));
          setPhase("declined");
        });
        source.addEventListener("expired", (event) => {
          setRequest(JSON.parse(event.data));
          setPhase("expired");
        });
        source.addEventListener("cancelled", (event) => {
          setRequest(JSON.parse(event.data));
          setPhase("cancelled");
        });
        source.addEventListener("request-state", (event) => {
          const current = JSON.parse(event.data);
          setRequest(current);
          if (current.status === "accepted") setPhase("accepted");
          if (["declined", "expired", "cancelled"].includes(current.status)) setPhase(current.status);
        });
        source.onerror = () => {
          if (!cancelled) setError("Connection to request updates was interrupted. Reconnecting...");
        };
      } catch (requestError) {
        if (!cancelled) {
          setError(requestError.message);
          setPhase("error");
        }
      }
    };

    createRequest();

    return () => {
      cancelled = true;
      if (source) source.close();
    };
  }, [teacher, token]);

  useEffect(() => {
    if (phase !== "waiting") return undefined;
    const interval = setInterval(() => {
      setCd((current) => {
        if (current <= 1) {
          clearInterval(interval);
          return 0;
        }
        return current - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [phase]);

  const cancelRequest = async () => {
    if (request?.id && phase === "waiting") {
      try {
        await apiRequest(`/api/video/requests/${request.id}`, { method: "DELETE", token });
      } catch {
        // Closing the modal should remain instant even if the request is already gone.
      }
    }
    onClose();
  };

  if (!teacher) return null;

  const waitingCopy = phase === "creating" ? "Creating lesson request..." : "Waiting for an available teacher to accept...";
  const terminalCopy = {
    declined: "The request was declined.",
    expired: "No teacher accepted in time.",
    cancelled: "The request was cancelled.",
    error: error || "Could not create a lesson request.",
  }[phase];

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1001,padding:20}}>
      <div style={{background:"#0f172a",border:"1px solid #1e293b",borderRadius:24,padding:28,width:"100%",maxWidth:380,textAlign:"center",animation:"slideUp 0.25s ease"}}>
        {(phase === "creating" || phase === "waiting") && <><div style={{width:72,height:72,borderRadius:"50%",background:"#052e16",display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,fontWeight:800,color:"#22c55e",margin:"0 auto 16px",position:"relative"}}>{teacher.av}<svg style={{position:"absolute",top:-10,left:-10,transform:"rotate(-90deg)"}} width="92" height="92" viewBox="0 0 92 92"><circle cx="46" cy="46" r="40" fill="none" stroke="#22c55e" strokeWidth="3" strokeDasharray={`${(cd/90)*251} 251`} strokeLinecap="round" style={{transition:"stroke-dasharray 1s linear"}}/></svg></div><h3 style={{fontSize:18,fontWeight:700,color:"#f1f5f9",marginBottom:6}}>Connecting to {teacher.name}</h3><p style={{color:"#64748b",fontSize:13,marginBottom:6}}>{waitingCopy}</p>{error&&<p style={{color:"#f59e0b",fontSize:12,marginBottom:6}}>{error}</p>}<p style={{color:"#22c55e",fontWeight:800,fontSize:22,marginBottom:20}}>{cd}s</p><button onClick={cancelRequest} style={{width:"100%",padding:"11px",border:"1px solid #1e293b",borderRadius:10,background:"transparent",fontWeight:600,fontSize:13,color:"#94a3b8",cursor:"pointer"}}>Cancel</button></>}
        {phase==="accepted"&&<><div style={{width:72,height:72,borderRadius:"50%",background:"#052e16",display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 16px"}}><svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg></div><h3 style={{fontSize:18,fontWeight:700,color:"#f1f5f9",marginBottom:6}}>Accepted</h3><p style={{color:"#64748b",fontSize:13,marginBottom:20}}>{request?.acceptedBy?.name || "A teacher"} is ready. Your real video room is open.</p><button disabled={!request?.roomId} onClick={()=>{onClose();onAccepted(teacher, request?.roomId);}} style={{width:"100%",padding:"13px",background:request?.roomId?"#22c55e":"#334155",color:"white",border:"none",borderRadius:12,fontSize:14,fontWeight:700,cursor:request?.roomId?"pointer":"wait"}}>Join Video Session</button></>}
        {terminalCopy&&<><div style={{width:72,height:72,borderRadius:"50%",background:"#1e293b",display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 16px",color:"#94a3b8",fontSize:26}}>!</div><h3 style={{fontSize:18,fontWeight:700,color:"#f1f5f9",marginBottom:8}}>Request not connected</h3><p style={{color:"#64748b",fontSize:13,marginBottom:20}}>{terminalCopy}</p><button onClick={onClose} style={{width:"100%",padding:"12px",background:"#7c3aed",color:"white",border:"none",borderRadius:10,fontWeight:700,cursor:"pointer"}}>Close</button></>}
      </div>
    </div>
  );
}

function DemoConnectFlow({ teacher, onClose, onAccepted }) {
  const [phase, setPhase] = useState("waiting");

  useEffect(() => {
    const timer = setTimeout(() => setPhase("accepted"), 1800);
    return () => clearTimeout(timer);
  }, []);

  if (!teacher) return null;

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1001,padding:20}}>
      <div style={{background:"#0f172a",border:"1px solid #1e293b",borderRadius:24,padding:28,width:"100%",maxWidth:380,textAlign:"center",animation:"slideUp 0.25s ease"}}>
        {phase === "waiting" ? (
          <>
            <div style={{width:72,height:72,borderRadius:"50%",background:"#052e16",display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,fontWeight:800,color:"#22c55e",margin:"0 auto 16px",position:"relative"}}>{teacher.av}<div style={{position:"absolute",inset:-10,border:"3px solid rgba(34,197,94,0.25)",borderTopColor:"#22c55e",borderRadius:"50%",animation:"spin 1s linear infinite"}} /></div>
            <h3 style={{fontSize:18,fontWeight:700,color:"#f1f5f9",marginBottom:6}}>Demo request to {teacher.name}</h3>
            <p style={{color:"#64748b",fontSize:13,marginBottom:20}}>Showing the instant lesson flow without creating a real account or video room.</p>
            <button onClick={onClose} style={{width:"100%",padding:"11px",border:"1px solid #1e293b",borderRadius:10,background:"transparent",fontWeight:600,fontSize:13,color:"#94a3b8",cursor:"pointer"}}>Cancel</button>
          </>
        ) : (
          <>
            <div style={{width:72,height:72,borderRadius:"50%",background:"#052e16",display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 16px"}}><svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg></div>
            <h3 style={{fontSize:18,fontWeight:700,color:"#f1f5f9",marginBottom:6}}>Teacher accepted</h3>
            <p style={{color:"#64748b",fontSize:13,marginBottom:20}}>Demo mode opens a local video session preview without creating a real account.</p>
            <button onClick={()=>{onClose();onAccepted?.(teacher, `demo-room-${teacher.id}-${Date.now()}`);}} style={{width:"100%",padding:"12px",background:"#22c55e",color:"white",border:"none",borderRadius:10,fontWeight:700,cursor:"pointer"}}>Join Demo Video Session</button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Book Modal ───────────────────────────────────────────────────────────────
function BookModal({ teacher, instrument, token, isDemoMode, trialAvailable, onClose, onBooked }) {
  const selectedInstrument = instrument || teacher?.instruments?.[0] || "Piano";
  const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const [form, setForm] = useState({ instrument: selectedInstrument, otherInstrument: "", date: tomorrow, time: "10:00", duration: "60 min", notes: "", isTrial: false });
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  if (!teacher && !instrument) return null;

  const bookingInstrument = form.instrument === OTHER_INSTRUMENT ? form.otherInstrument.trim() : form.instrument;
  const selectedDurationMinutes = getDurationMinutes(form.duration);
  const setTrialMode = () => {
    setForm((current) => ({
      ...current,
      isTrial: !current.isTrial,
      duration: !current.isTrial && getDurationMinutes(current.duration) > 45 ? "45 min" : current.duration,
    }));
  };

  const submitBooking = async () => {
    if (!bookingInstrument) {
      setError("Enter the instrument you want to learn.");
      return;
    }
    if (form.isTrial && selectedDurationMinutes > 45) {
      setError("Free trial lessons can be 45 minutes maximum.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      if (isDemoMode) {
        setDone({ status: teacher ? "confirmed" : "searching_teacher", emailNotice: "Demo: szukamy nauczyciela i potwierdzimy lekcje w ciagu 24 godzin." });
      } else {
        const payload = await apiRequest("/api/bookings", {
          method: "POST",
          token,
          body: {
            ...form,
            instrument: bookingInstrument,
            teacherId: teacher?.id || undefined,
            isTrial: form.isTrial,
          },
        });
        setDone(payload.booking);
        onBooked?.(payload.booking);
      }
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  const waitingForTeacher = done && done.status === "searching_teacher";
  const paymentPending = done && done.status === "payment_pending";
  const trialConfirmed = done && done.status === "trial_confirmed";
  if (done) return(<div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000,padding:20}}><div style={{background:"#0f172a",border:"1px solid #1e293b",borderRadius:20,padding:28,maxWidth:390,width:"100%",textAlign:"center"}}><div style={{width:64,height:64,borderRadius:"50%",background:waitingForTeacher||paymentPending?"#102a43":"#2e1065",display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 16px"}}><svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke={waitingForTeacher||paymentPending?"#60a5fa":"#a78bfa"} strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg></div><h3 style={{fontSize:18,fontWeight:700,color:"#f1f5f9",marginBottom:8}}>{waitingForTeacher ? "Lesson request saved" : trialConfirmed ? "Free trial booked" : paymentPending ? "Payment link sent" : "Lesson Booked!"}</h3><p style={{color:"#94a3b8",fontSize:13,lineHeight:1.6,marginBottom:20}}>{waitingForTeacher ? (done.isTrial ? "Szukamy nauczyciela do bezplatnej lekcji probnej. Trial zostal wykorzystany dla tego konta." : "Szukamy dla Ciebie nauczyciela. Dostaniesz potwierdzenie, czy lekcja sie odbedzie, w ciagu 24 godzin. Wyslalismy tez informacje na email.") : trialConfirmed ? "To jest bezplatna lekcja probna, bez platnosci Stripe. Administrator moze zresetowac mozliwosc triala." : paymentPending ? "Wyslalismy email z linkiem do platnosci. Po oplaceniu nauczyciel dostanie potwierdzenie, a lekcja zostanie zapisana w grafiku." : `Your lesson${teacher ? ` with ${teacher.name}` : ""} is confirmed.`}</p>{paymentPending&&done.payment?.checkoutUrl&&<a href={done.payment.checkoutUrl} style={{display:"block",width:"100%",padding:"12px",background:"#22c55e",color:"white",borderRadius:10,fontWeight:800,textDecoration:"none",marginBottom:10}}>Pay now</a>}<button onClick={onClose} style={{width:"100%",padding:"12px",background:"#7c3aed",color:"white",border:"none",borderRadius:10,fontWeight:700,cursor:"pointer"}}>Done</button></div></div>);
  return(<div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000,padding:20}} onClick={e=>{if(e.target===e.currentTarget)onClose();}}><div style={{background:"#0f172a",border:"1px solid #1e293b",borderRadius:20,padding:24,width:"100%",maxWidth:420,maxHeight:"90vh",overflowY:"auto"}}><div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18}}><h2 style={{fontSize:18,fontWeight:700,color:"#f1f5f9"}}>Book a Lesson</h2><button onClick={onClose} style={{background:"none",border:"none",fontSize:20,color:"#475569",cursor:"pointer"}}>×</button></div><div style={{display:"flex",gap:10,alignItems:"center",padding:12,background:"#1e293b",borderRadius:10,marginBottom:16}}><div style={{width:50,height:44,borderRadius:10,background:"#020617",position:"relative",overflow:"hidden",flexShrink:0}}>{teacher ? <TeacherCoverVisual teacher={teacher} alt={teacher.name} /> : <InstrumentFrameVisual instrument={form.instrument} alt={bookingInstrument || form.instrument} />}</div><div style={{flex:1}}><div style={{fontWeight:700,fontSize:14,color:"#f1f5f9"}}>{teacher?.name || `${bookingInstrument || form.instrument} lesson`}</div><div style={{fontSize:12,color:"#64748b"}}>{teacher ? teacher.instruments.join(", ") : "We will match a teacher after booking"}</div></div>{teacher && !form.isTrial && <div style={{fontWeight:800,fontSize:16,color:"#f1f5f9"}}>${teacher.price}</div>}{teacher && form.isTrial && <div style={{fontWeight:800,fontSize:13,color:"#86efac"}}>FREE</div>}</div>{trialAvailable&&<button type="button" onClick={setTrialMode} style={{width:"100%",padding:"10px 12px",border:`1px solid ${form.isTrial?"rgba(34,197,94,0.35)":"#1e293b"}`,borderRadius:10,background:form.isTrial?"rgba(34,197,94,0.1)":"transparent",color:form.isTrial?"#86efac":"#94a3b8",fontSize:13,fontWeight:800,cursor:"pointer",marginBottom:12}}>Use free trial lesson</button>}{!trialAvailable&&<div style={{background:"rgba(15,23,42,0.72)",border:"1px solid rgba(148,163,184,0.12)",borderRadius:10,padding:10,color:"#64748b",fontSize:12,lineHeight:1.5,marginBottom:12}}>Free trial lesson already used. Admin can reset it.</div>}<div style={{marginBottom:12}}><label style={{display:"block",fontSize:12,fontWeight:600,color:"#64748b",marginBottom:5}}>Instrument</label><select value={form.instrument} disabled={Boolean(teacher)} onChange={e=>setForm(current=>({...current,instrument:e.target.value,otherInstrument:e.target.value===OTHER_INSTRUMENT?current.otherInstrument:""}))} style={{width:"100%",padding:"9px 11px",border:"1px solid #1e293b",borderRadius:8,fontSize:13,background:"#1e293b",color:"#f1f5f9",cursor:teacher?"not-allowed":"pointer"}}>{INSTRUMENTS.map(o=><option key={o}>{o}</option>)}</select>{form.instrument===OTHER_INSTRUMENT&&<input value={form.otherInstrument} onChange={e=>setForm(current=>({...current,otherInstrument:e.target.value}))} placeholder="Type instrument, e.g. Cello" style={{width:"100%",marginTop:8,padding:"9px 11px",border:"1px solid #334155",borderRadius:8,fontSize:13,background:"#111c31",color:"#f1f5f9"}}/>}</div>{[["Date","date"],["Time","time"]].map(([lbl,type])=>(<div key={lbl} style={{marginBottom:12}}><label style={{display:"block",fontSize:12,fontWeight:600,color:"#64748b",marginBottom:5}}>{lbl}</label><input type={type} value={form[lbl.toLowerCase()]} onChange={e=>setForm(current=>({...current,[lbl.toLowerCase()]:e.target.value}))} style={{width:"100%",padding:"9px 11px",border:"1px solid #1e293b",borderRadius:8,fontSize:13,background:"#1e293b",color:"#f1f5f9"}}/></div>))}<div style={{marginBottom:12}}><label style={{display:"block",fontSize:12,fontWeight:600,color:"#64748b",marginBottom:5}}>Duration</label><select value={form.duration} onChange={e=>setForm(current=>({...current,duration:e.target.value}))} style={{width:"100%",padding:"9px 11px",border:"1px solid #1e293b",borderRadius:8,fontSize:13,background:"#1e293b",color:"#f1f5f9",cursor:"pointer"}}>{DURATION_OPTIONS.map(o=><option key={o} disabled={form.isTrial&&getDurationMinutes(o)>45} style={{color:form.isTrial&&getDurationMinutes(o)>45?"#64748b":"#f1f5f9",background:"#1e293b"}}>{o}</option>)}</select>{form.isTrial&&<div style={{fontSize:11,color:"#64748b",marginTop:6}}>Trial lessons are limited to 45 minutes.</div>}</div><div style={{marginBottom:16}}><label style={{display:"block",fontSize:12,fontWeight:600,color:"#64748b",marginBottom:5}}>Notes (optional)</label><textarea value={form.notes} onChange={e=>setForm(current=>({...current,notes:e.target.value}))} placeholder="Your level, goals, topics..." style={{width:"100%",padding:"9px 11px",border:"1px solid #1e293b",borderRadius:8,fontSize:13,minHeight:70,resize:"vertical",background:"#1e293b",color:"#f1f5f9"}}/></div>{!teacher&&<div style={{background:"rgba(59,130,246,0.08)",border:"1px solid rgba(96,165,250,0.18)",borderRadius:12,padding:12,color:"#bfdbfe",fontSize:12,lineHeight:1.5,marginBottom:14}}>{form.isTrial?"Trial jest bezplatny. Po bookowaniu nauczyciele zobacza request i moga go przyjac bez platnosci.":"Po bookowaniu szukamy nauczyciela i potwierdzimy w ciagu 24 godzin, czy lekcja sie odbedzie."}</div>}{error&&<div style={{background:"rgba(239,68,68,0.08)",border:"1px solid rgba(239,68,68,0.22)",borderRadius:10,padding:"9px 11px",color:"#fca5a5",fontSize:12,marginBottom:12}}>{error}</div>}<button disabled={busy || !bookingInstrument} onClick={submitBooking} style={{width:"100%",padding:"13px",background:busy || !bookingInstrument?"#334155":"#7c3aed",color:"white",border:"none",borderRadius:11,fontSize:14,fontWeight:700,cursor:busy?"wait":!bookingInstrument?"not-allowed":"pointer"}}>{busy ? "Booking..." : form.isTrial ? "Book free trial" : teacher ? `Confirm Booking - $${teacher.price}` : "Book and find a teacher"}</button></div></div>);
}

// ─── Video Session ────────────────────────────────────────────────────────────
function VideoSession({ session, token, user, onEnd }){
  const teacher = session.teacher;
  const roomId = session.roomId;
  const[elapsed,setElapsed]=useState(0);
  const[script,setScript]=useState(true);
  const[materialsOpen,setMaterialsOpen]=useState(true);
  const[activeStep,setActiveStep]=useState(2);
  const[copyState,setCopyState]=useState("Copy Link");
  const materials = getSessionMaterials(teacher);
  const video = useVideoRoom({ roomId, token, displayName: user?.name || "Participant" });
  useEffect(()=>{const t=setInterval(()=>setElapsed(e=>e+1),1000);return()=>clearInterval(t);},[]);
  const fmt=s=>`${String(Math.floor(s/60)).padStart(2,"0")}:${String(s%60).padStart(2,"0")}`;
  const steps=["Introduction & warm-up","C major scale – right hand","C major – both hands","Finger exercises 1-4","Simple melody practice","Review & feedback"];
  const roomUrl = `${window.location.origin}${window.location.pathname}?room=${encodeURIComponent(roomId)}`;
  const copyRoomLink = async () => {
    try {
      await navigator.clipboard.writeText(roomUrl);
      setCopyState("Copied");
      setTimeout(() => setCopyState("Copy Link"), 1400);
    } catch {
      setCopyState("Copy failed");
      setTimeout(() => setCopyState("Copy Link"), 1400);
    }
  };

  return(
    <div style={{position:"fixed",inset:0,background:"#020617",display:"flex",flexDirection:"column",zIndex:2000}}>
      <div style={{padding:"10px 18px",display:"flex",alignItems:"center",justifyContent:"space-between",borderBottom:"1px solid #0f172a"}}>
        <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}><div style={{width:8,height:8,borderRadius:"50%",background:video.connectionState==="connected"?"#22c55e":"#f59e0b",animation:"pulse 1.5s infinite"}}/><span style={{color:"white",fontWeight:600,fontSize:14}}>Live · {teacher.name}</span><span style={{color:"#334155",fontSize:13,fontFamily:"monospace"}}>{fmt(elapsed)}</span><span style={{color:"#64748b",fontSize:12}}>{video.status}</span></div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          <button onClick={copyRoomLink} style={{background:"transparent",color:"#94a3b8",border:"1px solid #1e293b",borderRadius:8,padding:"7px 12px",fontSize:12,fontWeight:600,cursor:"pointer"}}>{copyState}</button>
          <button onClick={onEnd} style={{background:"#ef4444",color:"white",border:"none",borderRadius:8,padding:"7px 14px",fontSize:13,fontWeight:600,cursor:"pointer"}}>End Session</button>
        </div>
      </div>
      <div style={{flex:1,display:"flex",overflow:"hidden"}}>
        <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",background:"#0f172a",position:"relative",overflow:"hidden"}}>
          <video ref={video.remoteVideoRef} autoPlay playsInline style={{width:"100%",height:"100%",objectFit:"cover",display:video.hasRemoteStream?"block":"none",background:"#020617"}} />
          {!video.hasRemoteStream && (
            <div style={{textAlign:"center",padding:24,maxWidth:360}}><div style={{width:100,height:100,borderRadius:"50%",background:"#1e293b",display:"flex",alignItems:"center",justifyContent:"center",fontSize:28,fontWeight:800,color:"#64748b",margin:"0 auto 12px"}}>{teacher.av || "MS"}</div><p style={{color:"#94a3b8",fontSize:14,fontWeight:700,marginBottom:6}}>{video.remoteName ? `Connecting with ${video.remoteName}` : "Waiting for another participant"}</p><p style={{color:"#475569",fontSize:12,lineHeight:1.5}}>{video.error || "Camera and microphone are active. Share the room link or wait for the accepted teacher to join."}</p></div>
          )}
          <div style={{position:"absolute",left:14,top:14,background:"rgba(2,6,23,0.64)",border:"1px solid rgba(148,163,184,0.16)",borderRadius:999,padding:"5px 10px",fontSize:11,color:"#cbd5e1"}}>{video.participants.length}/2 in room</div>
          <div style={{position:"absolute",bottom:12,right:12,width:170,height:112,background:"#020617",borderRadius:12,border:"1px solid #1e293b",overflow:"hidden",boxShadow:"0 16px 36px rgba(0,0,0,0.35)"}}>
            <video ref={video.localVideoRef} autoPlay playsInline muted style={{width:"100%",height:"100%",objectFit:"cover",display:"block",transform:"scaleX(-1)"}} />
            {!video.cameraEnabled && <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",background:"#020617",color:"#64748b",fontSize:12}}>Camera off</div>}
            <div style={{position:"absolute",left:8,bottom:6,fontSize:10,color:"#e2e8f0",textShadow:"0 1px 4px #000"}}>{user?.name || "You"}</div>
          </div>
        </div>
        {(script || materialsOpen) && (
          <div style={{width:290,background:"#0f172a",borderLeft:"1px solid #1e293b",padding:16,overflowY:"auto",display:"flex",flexDirection:"column",gap:16}}>
            {script && (
              <div>
                <div style={{fontSize:13,fontWeight:600,color:"#475569",marginBottom:14,display:"flex",justifyContent:"space-between"}}><span>Lesson Script</span><span style={{fontSize:10,color:"#334155"}}>Step {activeStep+1}/{steps.length}</span></div>
                {steps.map((s,i)=>(<div key={i} onClick={()=>setActiveStep(i)} style={{display:"flex",gap:8,marginBottom:8,padding:"8px 10px",borderRadius:8,background:i===activeStep?"rgba(124,58,237,0.12)":"transparent",border:i===activeStep?"1px solid rgba(124,58,237,0.25)":"1px solid transparent",cursor:"pointer"}}><div style={{width:20,height:20,borderRadius:"50%",background:i<activeStep?"#22c55e":i===activeStep?"#7c3aed":"#1e293b",display:"flex",alignItems:"center",justifyContent:"center",fontSize:9,color:"white",flexShrink:0,fontWeight:700}}>{i<activeStep?"✓":i+1}</div><span style={{fontSize:11,color:i<activeStep?"#334155":i===activeStep?"#c4b5fd":"#475569",textDecoration:i<activeStep?"line-through":"none",lineHeight:1.4}}>{s}</span></div>))}
              </div>
            )}

            {materialsOpen && (
              <div>
                <div style={{fontSize:13,fontWeight:600,color:"#475569",marginBottom:12,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <span>Teacher Materials</span>
                  <span style={{fontSize:10,color:"#334155"}}>{materials.length} files</span>
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:10}}>
                  {materials.map((material) => (
                    <div key={material.id} style={{background:"rgba(15,23,42,0.72)",border:"1px solid #1e293b",borderRadius:12,padding:12}}>
                      {material.type === "image" && material.preview && (
                        <div style={{position:"relative",borderRadius:10,overflow:"hidden",aspectRatio:"16 / 10",marginBottom:10}}>
                          <img src={material.preview} alt={material.title} style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}} />
                          <div style={{position:"absolute",inset:0,background:"linear-gradient(180deg, rgba(15,23,42,0.06), rgba(15,23,42,0.48))"}} />
                        </div>
                      )}
                      <div style={{display:"flex",alignItems:"flex-start",gap:10}}>
                        <div style={{width:32,height:32,borderRadius:10,background:material.type === "pdf" ? "rgba(124,58,237,0.18)" : "rgba(59,130,246,0.16)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:15,flexShrink:0}}>
                          {material.type === "pdf" ? "📄" : "🖼️"}
                        </div>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:13,fontWeight:700,color:"#f8fafc",marginBottom:4}}>{material.title}</div>
                          <div style={{fontSize:11,lineHeight:1.5,color:"#64748b",marginBottom:10}}>{material.subtitle}</div>
                          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                            <a href={material.href} target="_blank" rel="noreferrer" style={{padding:"6px 10px",borderRadius:8,background:"rgba(124,58,237,0.16)",border:"1px solid rgba(124,58,237,0.24)",color:"#d8b4fe",fontSize:11,fontWeight:700,textDecoration:"none"}}>Open</a>
                            <a href={material.href} download style={{padding:"6px 10px",borderRadius:8,background:"transparent",border:"1px solid #334155",color:"#94a3b8",fontSize:11,fontWeight:700,textDecoration:"none"}}>Download</a>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <div style={{padding:"12px 18px",display:"flex",alignItems:"center",justifyContent:"center",gap:8,borderTop:"1px solid #0f172a"}}>
        {[[video.micEnabled?"🎤":"🔇",video.micEnabled?"Mute":"Unmute",video.toggleMic,!video.micEnabled],[video.cameraEnabled?"📹":"🚫",video.cameraEnabled?"Camera":"Camera",video.toggleCamera,!video.cameraEnabled],[script?"📋":"📄",script?"Hide":"Script",()=>setScript(!script),script],[materialsOpen?"📚":"📎",materialsOpen?"Hide":"Materials",()=>setMaterialsOpen(!materialsOpen),materialsOpen]].map(([icon,lbl,action,active])=>(<button key={lbl} onClick={action} style={{display:"flex",flexDirection:"column",alignItems:"center",gap:3,padding:"8px 12px",background:active?"rgba(124,58,237,0.12)":"rgba(255,255,255,0.03)",border:active?"1px solid rgba(124,58,237,0.3)":"1px solid #1e293b",borderRadius:9,minWidth:56,cursor:"pointer"}}><span style={{fontSize:18}}>{icon}</span><span style={{fontSize:9,color:"#475569"}}>{lbl}</span></button>))}
      </div>
    </div>
  );
}

// ─── Teacher Dashboard ────────────────────────────────────────────────────────
function TeacherDashboard({ token, available, lessons = [], onAvailabilityChange, onAcceptRequest }){
  const[avail,setAvail]=useState(Boolean(available));
  const[requests,setRequests]=useState([]);
  const[bookings,setBookings]=useState([]);
  const[streamStatus,setStreamStatus]=useState("");

  useEffect(() => {
    setAvail(Boolean(available));
  }, [available]);

  useEffect(()=>{
    if(!avail || !token){
      setRequests([]);
      setStreamStatus("");
      return undefined;
    }

    const source = new EventSource(getSseUrl("/api/video/teacher/requests/events", { token }));
    source.addEventListener("requests", event => {
      setRequests(JSON.parse(event.data).requests || []);
      setStreamStatus("");
    });
    source.addEventListener("request-created", event => {
      const incoming = JSON.parse(event.data);
      setRequests(current => current.some(req => req.id === incoming.id) ? current : [incoming, ...current]);
      setStreamStatus("");
    });
    source.addEventListener("request-updated", event => {
      const updated = JSON.parse(event.data);
      setRequests(current => current.filter(req => req.id !== updated.id));
    });
    source.onerror = () => setStreamStatus("Reconnecting to lesson requests...");

    return () => source.close();
  },[avail, token]);

  const loadDynamicSchedule = useCallback(async () => {
    if (!token) return;
    try {
      const payload = await apiRequest("/api/bookings/teacher", { token });
      setBookings(payload.bookings || []);
    } catch (requestError) {
      setStreamStatus(requestError.message);
    }
  }, [token]);

  useEffect(() => {
    loadDynamicSchedule();
  }, [loadDynamicSchedule]);

  const handleAccept = async (req) => {
    try {
      const payload = await apiRequest(`/api/video/requests/${req.id}/accept`, { method: "POST", token });
      setRequests(current => current.filter(item => item.id !== req.id));
      onAcceptRequest?.({ request: payload.request, roomId: payload.roomId });
    } catch (requestError) {
      setStreamStatus(requestError.message);
    }
  };

  const handleDecline = async (req) => {
    try {
      await apiRequest(`/api/video/requests/${req.id}/decline`, { method: "POST", token });
      setRequests(current => current.filter(item => item.id !== req.id));
    } catch (requestError) {
      setStreamStatus(requestError.message);
    }
  };

  const handleClaimBooking = async (booking) => {
    try {
      const payload = await apiRequest(`/api/bookings/${booking.id}/claim`, { method: "POST", token });
      setBookings(current => current.map(item => item.id === booking.id ? payload.booking : item));
    } catch (requestError) {
      setStreamStatus(requestError.message);
    }
  };

  const handleRefundBooking = async (booking) => {
    try {
      const payload = await apiRequest(`/api/bookings/${booking.id}/refund`, {
        method: "POST",
        token,
        body: { reason: "Lesson did not happen" },
      });
      setBookings(current => current.map(item => item.id === booking.id ? payload.booking : item));
    } catch (requestError) {
      setStreamStatus(requestError.message);
    }
  };

  const handleCancelBooking = async (booking) => {
    try {
      const payload = await apiRequest(`/api/bookings/${booking.id}/cancel`, {
        method: "POST",
        token,
        body: { reason: "Teacher withdrew" },
      });
      setBookings(current => current.map(item => item.id === booking.id ? payload.booking : item));
    } catch (requestError) {
      setStreamStatus(requestError.message);
    }
  };

  const toggleAvailability = async () => {
    const next = !avail;
    setAvail(next);
    const saved = await onAvailabilityChange?.(next);
    if (!saved) setAvail(!next);
  };
  const openBookings = bookings.filter(booking => booking.status === "searching_teacher");
  const acceptedBookings = bookings.filter(booking => booking.status !== "searching_teacher");
  return(
    <div>
      <div style={{background:"#0f172a",borderRadius:14,border:"1px solid #1e293b",padding:20,marginBottom:16}}>
        <h3 style={{fontSize:15,fontWeight:700,color:"#f1f5f9",marginBottom:14}}>Availability Mode</h3>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"14px 16px",background:avail?"rgba(34,197,94,0.06)":"#1e293b",borderRadius:10,border:`1px solid ${avail?"rgba(34,197,94,0.2)":"#334155"}`}}>
          <div><div style={{fontWeight:600,fontSize:14,color:"#f1f5f9",marginBottom:3}}>{avail?"🟢 You're available now":"⚫ You're offline"}</div><div style={{fontSize:12,color:"#475569"}}>{avail?"Students can send you instant connect requests":"Toggle to start accepting requests"}</div></div>
          <div onClick={toggleAvailability} style={{width:48,height:26,borderRadius:13,background:avail?"#22c55e":"#334155",cursor:"pointer",position:"relative",transition:"background 0.2s",flexShrink:0}}><div style={{position:"absolute",top:3,left:avail?25:3,width:20,height:20,borderRadius:"50%",background:"white",transition:"left 0.2s",boxShadow:"0 1px 3px rgba(0,0,0,0.3)"}}/></div>
        </div>
      </div>
      {streamStatus&&<div style={{background:"rgba(245,158,11,0.08)",border:"1px solid rgba(245,158,11,0.22)",borderRadius:12,padding:12,color:"#fbbf24",fontSize:12,marginBottom:16}}>{streamStatus}</div>}
      {requests.map(req=>(<div key={req.id} style={{background:"#0f172a",borderRadius:14,border:"2px solid #22c55e",padding:20,marginBottom:16,animation:"slideUp 0.3s ease"}}><div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}><div style={{width:8,height:8,borderRadius:"50%",background:"#22c55e",animation:"pulse 1s infinite"}}/><span style={{fontSize:14,fontWeight:700,color:"#f1f5f9"}}>Incoming Request</span></div><p style={{fontSize:13,color:"#94a3b8",marginBottom:3}}><b style={{color:"#f1f5f9"}}>{req.student?.name || "Student"}</b> wants an instant lesson with {req.teacherName}</p><p style={{fontSize:12,color:"#475569",marginBottom:14}}>Topic: {req.topic}</p><div style={{display:"flex",gap:8}}><button onClick={()=>handleAccept(req)} style={{flex:1,padding:"10px",background:"#22c55e",color:"white",border:"none",borderRadius:9,fontWeight:700,fontSize:13,cursor:"pointer"}}>Accept</button><button onClick={()=>handleDecline(req)} style={{flex:1,padding:"10px",background:"transparent",color:"#94a3b8",border:"1px solid #1e293b",borderRadius:9,fontWeight:600,fontSize:13,cursor:"pointer"}}>Decline</button></div></div>))}
      {avail && requests.length===0 && <div style={{background:"#0f172a",borderRadius:14,border:"1px solid #1e293b",padding:16,marginBottom:16,color:"#64748b",fontSize:13}}>No live requests yet.</div>}
      <div style={{background:"#0f172a",borderRadius:14,border:"1px solid #1e293b",padding:20,marginBottom:16}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,marginBottom:14}}>
          <h3 style={{fontSize:15,fontWeight:700,color:"#f1f5f9"}}>Dynamic Schedule</h3>
          <button onClick={loadDynamicSchedule} style={{padding:"7px 10px",background:"transparent",color:"#94a3b8",border:"1px solid #1e293b",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer"}}>Refresh</button>
        </div>
        {openBookings.length > 0 ? openBookings.map(booking => <LessonBookingCard key={booking.id} booking={booking} role="teacher" onClaim={handleClaimBooking} />) : <div style={{color:"#64748b",fontSize:13}}>No open instrument bookings yet.</div>}
      </div>
      <div style={{background:"#0f172a",borderRadius:14,border:"1px solid #1e293b",padding:20,marginBottom:16}}>
        <h3 style={{fontSize:15,fontWeight:700,color:"#f1f5f9",marginBottom:14}}>My Accepted Lessons</h3>
        {acceptedBookings.length > 0 ? acceptedBookings.map(booking => <LessonBookingCard key={booking.id} booking={booking} role="teacher" onCancel={handleCancelBooking} onRefund={handleRefundBooking} />) : <div style={{color:"#64748b",fontSize:13}}>No accepted lessons yet.</div>}
      </div>
      <div style={{background:"#0f172a",borderRadius:14,border:"1px solid #1e293b",padding:20}}><h3 style={{fontSize:15,fontWeight:700,color:"#f1f5f9",marginBottom:14}}>Upcoming Lessons</h3>{lessons.filter(l=>l.status==="upcoming").length > 0 ? lessons.filter(l=>l.status==="upcoming").map(l=>(<div key={l.id} style={{padding:"12px 0",borderBottom:"1px solid #0f172a",display:"flex",justifyContent:"space-between",alignItems:"center"}}><div><div style={{fontWeight:600,fontSize:13,color:"#f1f5f9"}}>{l.title}</div><div style={{fontSize:12,color:"#475569",marginTop:2}}>{l.date} at {l.time}</div></div><span style={{background:"#1e3a5f",color:"#60a5fa",padding:"3px 9px",borderRadius:20,fontSize:11,fontWeight:600}}>{l.instrument}</span></div>)) : <div style={{color:"#64748b",fontSize:13}}>No saved live lessons yet. Instant requests will appear above while availability is on.</div>}</div>
    </div>
  );
}

function TeacherProfilePanel({ profile, onSave, saving, saveError }) {
  const [form, setForm] = useState(() => ({
    instruments: profile?.instruments?.length ? profile.instruments : ["Piano"],
    level: profile?.level || "Beginner",
    lang: profile?.lang || "Polish",
    price: profile?.price ?? 60,
    bio: profile?.bio || "",
    tags: (profile?.tags || []).join(", "),
    photoDataUrl: profile?.photoDataUrl || "",
    cvDataUrl: profile?.cvDataUrl || "",
    cvFileName: profile?.cvFileName || "",
  }));
  const [localError, setLocalError] = useState("");

  useEffect(() => {
    setForm({
      instruments: profile?.instruments?.length ? profile.instruments : ["Piano"],
      level: profile?.level || "Beginner",
      lang: profile?.lang || "Polish",
      price: profile?.price ?? 60,
      bio: profile?.bio || "",
      tags: (profile?.tags || []).join(", "),
      photoDataUrl: profile?.photoDataUrl || "",
      cvDataUrl: profile?.cvDataUrl || "",
      cvFileName: profile?.cvFileName || "",
    });
  }, [profile]);

  const toggleInstrument = (instrument) => {
    setForm((current) => {
      const exists = current.instruments.includes(instrument);
      return {
        ...current,
        instruments: exists
          ? current.instruments.filter((item) => item !== instrument)
          : [...current.instruments, instrument],
      };
    });
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await readFileAsDataUrl(file);
      setLocalError("");
      setForm((current) => ({ ...current, photoDataUrl: dataUrl }));
    } catch (error) {
      setLocalError(error.message);
    }
  };

  const handleCvUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await readFileAsDataUrl(file);
      setLocalError("");
      setForm((current) => ({ ...current, cvDataUrl: dataUrl, cvFileName: file.name }));
    } catch (error) {
      setLocalError(error.message);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLocalError("");
    if (form.instruments.length === 0) {
      setLocalError("Choose at least one instrument.");
      return;
    }
    await onSave({
      instruments: form.instruments,
      level: form.level,
      lang: form.lang,
      price: Number(form.price),
      bio: form.bio,
      tags: form.tags.split(",").map((item) => item.trim()).filter(Boolean),
      photoDataUrl: form.photoDataUrl,
      cvDataUrl: form.cvDataUrl,
      cvFileName: form.cvFileName,
      availabilityMode: Boolean(profile?.status === "available"),
      status: profile?.status || "offline",
    });
  };

  return (
    <form onSubmit={handleSubmit} style={{ background:"#0f172a", borderRadius:14, border:"1px solid #1e293b", padding:20, marginBottom:16 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:12, marginBottom:16, flexWrap:"wrap" }}>
        <div>
          <h3 style={{fontSize:15,fontWeight:700,color:"#f1f5f9",marginBottom:4}}>Public Profile</h3>
          <div style={{fontSize:12,color:"#64748b"}}>Students will see this profile in the teacher directory and detail dialog.</div>
        </div>
        <button type="submit" disabled={saving} style={{ padding:"10px 14px", background:saving ? "#334155" : "#22c55e", color:"white", border:"none", borderRadius:10, fontSize:13, fontWeight:700, cursor:saving ? "wait" : "pointer" }}>{saving ? "Saving..." : "Save profile"}</button>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))", gap:16 }}>
        <div>
          <label style={{display:"block",fontSize:12,fontWeight:600,color:"#64748b",marginBottom:8}}>Instruments</label>
          <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
            {INSTRUMENTS.map((instrument) => (
              <button key={instrument} type="button" onClick={() => toggleInstrument(instrument)} style={{ padding:"8px 12px", borderRadius:999, border:`1px solid ${form.instruments.includes(instrument) ? "#22c55e" : "#334155"}`, background:form.instruments.includes(instrument) ? "rgba(34,197,94,0.12)" : "transparent", color:form.instruments.includes(instrument) ? "#bbf7d0" : "#94a3b8", fontSize:12, fontWeight:700, cursor:"pointer" }}>
                {instrument}
              </button>
            ))}
          </div>
        </div>

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10 }}>
          <div>
            <label style={{display:"block",fontSize:12,fontWeight:600,color:"#64748b",marginBottom:5}}>Level</label>
            <select value={form.level} onChange={e => setForm(current => ({ ...current, level: e.target.value }))} style={{width:"100%",padding:"9px 11px",border:"1px solid #1e293b",borderRadius:8,fontSize:13,background:"#1e293b",color:"#f1f5f9"}}>
              {["Beginner", "Intermediate", "Advanced", "Professional"].map((level) => <option key={level}>{level}</option>)}
            </select>
          </div>
          <div>
            <label style={{display:"block",fontSize:12,fontWeight:600,color:"#64748b",marginBottom:5}}>Language</label>
            <input value={form.lang} onChange={e => setForm(current => ({ ...current, lang: e.target.value }))} style={{width:"100%",padding:"9px 11px",border:"1px solid #1e293b",borderRadius:8,fontSize:13,background:"#1e293b",color:"#f1f5f9"}} />
          </div>
          <div>
            <label style={{display:"block",fontSize:12,fontWeight:600,color:"#64748b",marginBottom:5}}>Price</label>
            <input type="number" min="0" value={form.price} onChange={e => setForm(current => ({ ...current, price: e.target.value }))} style={{width:"100%",padding:"9px 11px",border:"1px solid #1e293b",borderRadius:8,fontSize:13,background:"#1e293b",color:"#f1f5f9"}} />
          </div>
        </div>

        <div style={{ gridColumn:"1 / -1" }}>
          <label style={{display:"block",fontSize:12,fontWeight:600,color:"#64748b",marginBottom:5}}>Bio</label>
          <textarea value={form.bio} onChange={e => setForm(current => ({ ...current, bio: e.target.value }))} placeholder="Describe your background, teaching style and who you work best with." style={{width:"100%",padding:"10px 12px",border:"1px solid #1e293b",borderRadius:8,fontSize:13,minHeight:110,resize:"vertical",background:"#1e293b",color:"#f1f5f9"}} />
        </div>

        <div style={{ gridColumn:"1 / -1" }}>
          <label style={{display:"block",fontSize:12,fontWeight:600,color:"#64748b",marginBottom:5}}>Specialties</label>
          <input value={form.tags} onChange={e => setForm(current => ({ ...current, tags: e.target.value }))} placeholder="Classical, Jazz, Improvisation" style={{width:"100%",padding:"9px 11px",border:"1px solid #1e293b",borderRadius:8,fontSize:13,background:"#1e293b",color:"#f1f5f9"}} />
        </div>

        <div>
          <label style={{display:"block",fontSize:12,fontWeight:600,color:"#64748b",marginBottom:8}}>Profile photo</label>
          <input type="file" accept="image/*" onChange={handleImageUpload} style={{width:"100%",fontSize:12,color:"#94a3b8"}} />
          {form.photoDataUrl && <img src={form.photoDataUrl} alt="Teacher profile preview" style={{ marginTop:12, width:"100%", maxWidth:220, aspectRatio:"3 / 2", objectFit:"cover", borderRadius:12, border:"1px solid #1e293b", display:"block" }} />}
        </div>

        <div>
          <label style={{display:"block",fontSize:12,fontWeight:600,color:"#64748b",marginBottom:8}}>CV file</label>
          <input type="file" accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" onChange={handleCvUpload} style={{width:"100%",fontSize:12,color:"#94a3b8"}} />
          <div style={{ marginTop:12, fontSize:12, color:"#94a3b8" }}>{form.cvFileName || "No CV uploaded yet."}</div>
        </div>
      </div>

      {(localError || saveError) && <div style={{ background:"rgba(239,68,68,0.08)", border:"1px solid rgba(239,68,68,0.22)", borderRadius:10, padding:"10px 12px", color:"#fca5a5", fontSize:12, marginTop:16 }}>{localError || saveError}</div>}
    </form>
  );
}

// ─── Main App ─────────────────────────────────────────────────────────────────
export default function MusicSpace() {
  const [page, setPage] = useState("home");
  const [appMode, setAppMode] = useState(readStoredAppMode);
  const [language, setLanguage] = useState(readStoredLanguage);
  const [modal, setModal] = useState(null);
  const [loginMode, setLoginMode] = useState("login");
  const [connectT, setConnectT] = useState(null);
  const [bookT, setBookT] = useState(null);
  const [bookInstrument, setBookInstrument] = useState(null);
  const [session, setSession] = useState(null);
  const [cvTeacher, setCvTeacher] = useState(null);
  const [filterI, setFilterI] = useState("All");
  const [filterS, setFilterS] = useState("All");
  const [search, setSearch] = useState("");
  const [authForm, setAuthForm] = useState({ name: "", email: "", password: "", role: "student", instrument: "Piano" });
  const [authBusy, setAuthBusy] = useState(false);
  const [authSubmitError, setAuthSubmitError] = useState("");
  const [authNotice, setAuthNotice] = useState("");
  const [captchaToken, setCaptchaToken] = useState("");
  const [resetForm, setResetForm] = useState({ email: "", token: "", password: "" });
  const [directoryTeachers, setDirectoryTeachers] = useState([]);
  const [teacherProfile, setTeacherProfile] = useState(null);
  const [teacherProfileBusy, setTeacherProfileBusy] = useState(false);
  const [teacherProfileError, setTeacherProfileError] = useState("");
  const [studentBookings, setStudentBookings] = useState([]);
  const [trialAvailable, setTrialAvailable] = useState(false);
  const [adminStudents, setAdminStudents] = useState([]);
  const [adminError, setAdminError] = useState("");
  const [showHomeAudioControls, setShowHomeAudioControls] = useState(false);
  const [pendingScrollTarget, setPendingScrollTarget] = useState("");
  const [openFaq, setOpenFaq] = useState("booking");

  const { playing, toggle: toggleMusic, volume, changeVolume } = useAmbientMusic();
  const { user, token, loading: authLoading, login, register, logout } = useAuth();
  const isDemoMode = appMode === "demo";
  const role = user?.role || null;
  const t = useCallback((key) => getTranslation(language, key), [language]);
  const teachers = isDemoMode ? TEACHERS.map((teacher) => normalizeTeacher(teacher)) : directoryTeachers.map((teacher) => normalizeTeacher(teacher));
  const liveTeacherCount = directoryTeachers.length;

  const filtered = teachers.filter(t => {
    if (filterI !== "All" && !t.instruments.includes(filterI)) return false;
    if (filterS === "Available" && t.status !== "available") return false;
    if (search && !t.name.toLowerCase().includes(search.toLowerCase()) && !t.instruments.some(i => i.toLowerCase().includes(search.toLowerCase()))) return false;
    return true;
  }).sort((a, b) => ({ available:0, busy:1, offline:2 }[a.status] - { available:0, busy:1, offline:2 }[b.status]));

  const openSession = useCallback((teacher, roomId) => {
    const roomTeacher = {
      av: teacher.av || "MS",
      instruments: teacher.instruments || ["Music"],
      ...teacher,
    };
    setSession({ teacher: roomTeacher, roomId });
    window.history.replaceState(null, "", `${window.location.pathname}?room=${encodeURIComponent(roomId)}`);
  }, []);

  const loadTeachers = useCallback(async () => {
    try {
      const payload = await apiRequest("/api/teachers");
      setDirectoryTeachers(payload.teachers || []);
    } catch {
      setDirectoryTeachers([]);
    }
  }, []);

  const loadStudentBookings = useCallback(async () => {
    if (!token || role !== "student" || isDemoMode) {
      setStudentBookings([]);
      setTrialAvailable(false);
      return;
    }
    try {
      const payload = await apiRequest("/api/bookings/me", { token });
      setStudentBookings(payload.bookings || []);
      setTrialAvailable(Boolean(payload.trialAvailable));
    } catch {
      setStudentBookings([]);
      setTrialAvailable(false);
    }
  }, [isDemoMode, role, token]);

  const loadAdminStudents = useCallback(async () => {
    if (!token || role !== "admin" || isDemoMode) {
      setAdminStudents([]);
      return;
    }
    try {
      const payload = await apiRequest("/api/admin/students", { token });
      setAdminStudents(payload.students || []);
      setAdminError("");
    } catch (error) {
      setAdminError(error.message);
    }
  }, [isDemoMode, role, token]);

  const closeSession = () => {
    setSession(null);
    window.history.replaceState(null, "", window.location.pathname);
  };

  useEffect(() => {
    loadTeachers();
  }, [loadTeachers, role]);

  useEffect(() => {
    loadStudentBookings();
  }, [loadStudentBookings, page]);

  useEffect(() => {
    loadAdminStudents();
  }, [loadAdminStudents, page]);

  useEffect(() => {
    if (page !== "home") {
      setShowHomeAudioControls(false);
      return undefined;
    }

    const updateHomeAudioControls = () => {
      const hero = document.querySelector(".home-hero");
      const threshold = hero ? hero.offsetTop + hero.offsetHeight - 140 : 420;
      setShowHomeAudioControls(window.scrollY > threshold);
    };

    updateHomeAudioControls();
    window.addEventListener("scroll", updateHomeAudioControls, { passive: true });
    window.addEventListener("resize", updateHomeAudioControls);
    return () => {
      window.removeEventListener("scroll", updateHomeAudioControls);
      window.removeEventListener("resize", updateHomeAudioControls);
    };
  }, [page]);

  useEffect(() => {
    if (!pendingScrollTarget) return undefined;
    const timer = window.setTimeout(() => {
      document.getElementById(pendingScrollTarget)?.scrollIntoView({ behavior: "smooth", block: "start" });
      setPendingScrollTarget("");
    }, 80);
    return () => window.clearTimeout(timer);
  }, [page, pendingScrollTarget]);

  const changeAppMode = (nextMode) => {
    setAppMode(nextMode);
    storeAppMode(nextMode);
    setModal(null);
    setConnectT(null);
    setBookT(null);
    setBookInstrument(null);
    setCvTeacher(null);
    setSearch("");
    setFilterI("All");
    setFilterS("All");
    if (nextMode === "demo" && ["dashboard", "admin"].includes(page)) setPage("home");
    if (nextMode === "live" && page === "lessons" && role !== "student") setPage(role === "teacher" ? "dashboard" : "home");
  };

  const changeLanguage = (nextLanguage) => {
    setLanguage(nextLanguage);
    storeLanguage(nextLanguage);
  };

  useEffect(() => {
    if (!token || role !== "teacher") {
      setTeacherProfile(null);
      setTeacherProfileError("");
      return;
    }

    let cancelled = false;
    apiRequest("/api/teacher-profile/me", { token })
      .then((payload) => {
        if (!cancelled) {
          setTeacherProfile(payload.profile);
          setTeacherProfileError("");
        }
      })
      .catch((error) => {
        if (!cancelled) setTeacherProfileError(error.message);
      });

    return () => {
      cancelled = true;
    };
  }, [role, token]);

  useEffect(() => {
    if (authLoading || session) return;
    const roomId = new URLSearchParams(window.location.search).get("room");
    if (!roomId) return;
    if (!token) {
      setModal("login");
      setLoginMode("login");
      return;
    }

    let cancelled = false;
    apiRequest(`/api/video/rooms/${roomId}`, { token })
      .then(({ room }) => {
        if (!cancelled) {
          openSession({
            id: room.teacher?.id || room.id,
            name: room.teacher?.name || "Music Space Session",
            av: "MS",
            instruments: ["Music"],
          }, room.id);
        }
      })
      .catch(() => {
        if (!cancelled) window.history.replaceState(null, "", window.location.pathname);
      });
    return () => {
      cancelled = true;
    };
  }, [authLoading, openSession, session, token]);

  useEffect(() => {
    const resetToken = new URLSearchParams(window.location.search).get("resetToken");
    if (!resetToken) return;
    setResetForm((form) => ({ ...form, token: resetToken }));
    setLoginMode("reset");
    setModal("login");
  }, []);

  const handleAuthSubmit = async (event) => {
    event.preventDefault();
    setAuthBusy(true);
    setAuthSubmitError("");
    setAuthNotice("");
    try {
      if (loginMode === "forgot") {
        const payload = await apiRequest("/api/auth/password-reset/request", {
          method: "POST",
          body: { email: authForm.email || resetForm.email, captchaToken },
        });
        setAuthNotice(payload.message || "If this email exists, a password reset link has been sent.");
        return;
      }

      if (loginMode === "reset") {
        const payload = await apiRequest("/api/auth/password-reset/confirm", {
          method: "POST",
          body: { token: resetForm.token, password: resetForm.password, captchaToken },
        });
        setAuthNotice(payload.message || "Password has been updated. You can log in now.");
        setResetForm({ email: "", token: "", password: "" });
        setLoginMode("login");
        window.history.replaceState(null, "", window.location.pathname);
        return;
      }

      const payload = loginMode === "login"
        ? await login({ email: authForm.email, password: authForm.password, captchaToken })
        : await register({
            name: authForm.name,
            email: authForm.email,
            password: authForm.password,
            role: authForm.role,
            instrument: authForm.role === "teacher" ? authForm.instrument : undefined,
            captchaToken,
          });
      setModal(null);
      setAuthForm({ name: "", email: "", password: "", role: "student", instrument: "Piano" });
      setCaptchaToken("");
      setPage(payload.user.role === "teacher" ? "dashboard" : payload.user.role === "admin" ? "admin" : "teachers");
      loadTeachers();
    } catch (requestError) {
      setAuthSubmitError(requestError.message);
    } finally {
      setAuthBusy(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    setSession(null);
    setModal(null);
    setPage("home");
    window.history.replaceState(null, "", window.location.pathname);
  };

  const saveTeacherProfile = async (profileInput) => {
    setTeacherProfileBusy(true);
    setTeacherProfileError("");
    try {
      const payload = await apiRequest("/api/teacher-profile/me", {
        method: "PUT",
        token,
        body: profileInput,
      });
      setTeacherProfile(payload.profile);
      setCvTeacher((current) => current && current.id === payload.profile.id ? payload.profile : current);
      await loadTeachers();
      return payload.profile;
    } catch (error) {
      setTeacherProfileError(error.message);
      return null;
    } finally {
      setTeacherProfileBusy(false);
    }
  };

  const updateTeacherAvailability = async (available) => {
    if (!teacherProfile) return;
    return saveTeacherProfile({
      ...teacherProfile,
      tags: teacherProfile.tags || [],
      availabilityMode: available,
      status: available ? "available" : "offline",
    });
  };

  const startDirectRoom = useCallback(async (teacher) => {
    if (isDemoMode) {
      setConnectT(teacher);
      setModal("demo-connect");
      return;
    }
    if (!token) {
      setModal("login");
      setLoginMode("login");
      return;
    }
    try {
      const payload = await apiRequest("/api/video/rooms", {
        method: "POST",
        token,
        body: { teacher },
      });
      openSession(teacher, payload.roomId);
    } catch (requestError) {
      setAuthSubmitError(requestError.message);
      setModal("login");
      setLoginMode("login");
    }
  }, [isDemoMode, openSession, token]);

  const openTeacherBooking = (teacher) => {
    if (!isDemoMode && !token) {
      setModal("login");
      setLoginMode("login");
      return;
    }
    setBookT(teacher);
    setBookInstrument(null);
    setModal("book");
  };

  const openInstrumentBooking = (instrument) => {
    if (!isDemoMode && !token) {
      setModal("login");
      setLoginMode("login");
      return;
    }
    setBookT(null);
    setBookInstrument(instrument);
    setModal("book");
  };

  const scrollToAnchor = (id) => {
    window.setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
  };

  const goToTeacherSearch = (event) => {
    event?.preventDefault();
    setFilterI("All");
    setFilterS("All");
    setSearch("");
    setPendingScrollTarget("find-teachers");
    setPage("teachers");
    if (page === "teachers") scrollToAnchor("find-teachers");
  };

  const showUserProfile = (event) => {
    event?.preventDefault();
    if (role === "teacher") {
      setPage("dashboard");
      scrollToAnchor("user-profile");
      return;
    }
    setModal("user-profile");
  };

  const cancelStudentBooking = async (booking) => {
    try {
      const payload = await apiRequest(`/api/bookings/${booking.id}/cancel`, {
        method: "POST",
        token,
        body: { reason: "Student cancelled" },
      });
      setStudentBookings(current => current.map(item => item.id === booking.id ? payload.booking : item));
      loadStudentBookings();
    } catch (requestError) {
      setAuthSubmitError(requestError.message);
    }
  };

  const resetStudentTrial = async (studentId) => {
    try {
      const payload = await apiRequest(`/api/admin/students/${studentId}/trial-reset`, { method: "POST", token });
      setAdminStudents(current => current.map(student => student.id === studentId ? payload.student : student));
      setAdminError("");
    } catch (error) {
      setAdminError(error.message);
    }
  };

  if (session) return <VideoSession session={session} token={token} user={user} onEnd={closeSession} />;
  const navItems = isDemoMode
    ? [["teachers", t("findTeachers")], ["lessons", t("myLessons")]]
    : role === "student" ? [["teachers", t("findTeachers")], ["lessons", t("myLessons")]] : role === "teacher" ? [["dashboard", t("dashboard")], ["teachers", t("browse")]] : role === "admin" ? [["admin", t("admin")]] : [];
  const handleStartFree = () => {
    if (role) {
      setPage(role === "teacher" ? "dashboard" : "teachers");
      return;
    }
    if (isDemoMode) changeAppMode("live");
    setLoginMode("signup");
    setModal("login");
  };
  const handleBrowseTeachers = () => {
    goToTeacherSearch();
  };
  const howItWorksSteps = [
    ["🔍", t("findATeacher"), t("findTeacherDesc")],
    ["⚡", t("connectInstantly"), t("connectInstantlyDesc")],
    ["📅", t("bookAhead"), t("bookAheadDesc")],
    ["📈", t("trackGrowth"), t("trackGrowthDesc")],
  ];
  const faqItems = [
    {
      id: "booking",
      question: "Jak zabookować lekcję?",
      answer: "Wybierasz instrument albo konkretnego nauczyciela, klikasz Book Lesson, ustawiasz datę, godzinę, długość lekcji i opcjonalne notatki. Jeśli nie wybierzesz nauczyciela, Music Space zapisze request i pokaże go nauczycielom, którzy mogą go przyjąć.",
    },
    {
      id: "trial",
      question: "Jak działa lekcja próbna?",
      answer: "Uczeń może użyć darmowej lekcji próbnej, jeśli trial jest jeszcze dostępny na koncie. Trial bookuje się bez płatności Stripe, nauczyciele widzą request i mogą go zaakceptować. Po wykorzystaniu triala administrator może go ręcznie zresetować.",
    },
    {
      id: "instant",
      question: "Jak połączyć się od razu z żywym nauczycielem?",
      answer: "Na karcie dostępnego nauczyciela klikasz Connect Now. System tworzy request instant lesson i czeka na akceptację nauczyciela. Po akceptacji otwiera się pokój wideo, do którego uczeń i nauczyciel mogą wejść od razu.",
    },
    {
      id: "payment",
      question: "Kiedy i jak płaci uczeń?",
      answer: "Dla płatnej lekcji system wysyła link do płatności. Po opłaceniu lekcja dostaje status potwierdzony, a nauczyciel widzi ją w swoich zaakceptowanych lekcjach. Lekcja próbna omija płatność i jest oznaczona jako free trial.",
    },
    {
      id: "acceptance",
      question: "Co dzieje się po akceptacji nauczyciela?",
      answer: "Jeśli request był bez konkretnego nauczyciela, nauczyciel klika I can take it. Wtedy lekcja zostaje przypisana do niego, uczeń widzi nauczyciela na karcie lekcji, a dalszy flow zależy od statusu płatności lub triala.",
    },
    {
      id: "refund",
      question: "Co jeśli lekcja się nie odbędzie?",
      answer: "Jeśli lekcja jest odwołana przed realizacją, płatność wraca do ucznia zgodnie ze statusem Stripe. Jeśli lekcja była opłacona, ale się nie odbyła, uczeń klika na karcie lekcji, że lekcja się nie odbyła. Nauczyciel potwierdza i środki wracają do ucznia.",
    },
    {
      id: "dispute",
      question: "Co jeśli nauczyciel nie potwierdzi zwrotu?",
      answer: "Wtedy zgłoszenie przechodzi do reklamacji. Administrator rozpatruje sprawę w ciągu 24 godzin na podstawie statusów lekcji, płatności i informacji od obu stron. Po pozytywnym rozpatrzeniu środki wracają do ucznia.",
    },
  ];

  return (
    <div style={{ fontFamily: "'Segoe UI',system-ui,sans-serif", minHeight: "100vh", background: "#0b0e16", color: "#e2e8f0" }}>
      <style>{`
        @keyframes pulse{0%,100%{box-shadow:0 0 0 0 rgba(34,197,94,0.4)}50%{box-shadow:0 0 0 6px rgba(34,197,94,0)}}
        @keyframes slideUp{from{transform:translateY(20px);opacity:0}to{transform:translateY(0);opacity:1}}
        @keyframes drift1{0%,100%{transform:translate(0,0)}50%{transform:translate(60px,40px)}}
        @keyframes drift2{0%,100%{transform:translate(0,0)}50%{transform:translate(-50px,-30px)}}
        @keyframes ripple{0%{transform:scale(1);opacity:0.6}100%{transform:scale(1.8);opacity:0}}
        @keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
        *{box-sizing:border-box;margin:0;padding:0}
        input,select,textarea,button{font-family:inherit;outline:none}
        ::-webkit-scrollbar{width:5px}::-webkit-scrollbar-thumb{background:#1e293b;border-radius:3px}
        @media (max-width: 760px) {
          .topbar-inner { height: auto !important; min-height: 58px; padding-top: 10px !important; padding-bottom: 10px !important; flex-wrap: wrap; gap: 10px; }
          .topbar-left, .topbar-actions { flex-wrap: wrap; }
          .home-hero { min-height: 680px !important; align-items: flex-start !important; padding-top: 58px !important; }
          .home-hero-copy { margin: 0 auto !important; text-align: center !important; }
          .home-hero-copy h1 { font-size: clamp(32px, 11vw, 48px) !important; }
          .home-hero-menu { grid-template-columns: 1fr !important; }
          .how-it-works-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
          .onboarding-grid { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 520px) {
          .how-it-works-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      {/* AMBIENT MUSIC BUTTON */}
      {(page !== "home" || showHomeAudioControls) && <AudioButton playing={playing} onToggle={toggleMusic} volume={volume} onVolume={changeVolume} />}

      {/* NAV */}
      <nav style={{ background: "rgba(11,14,22,0.85)", backdropFilter: "blur(12px)", borderBottom: "1px solid #0f172a", position: "sticky", top: 0, zIndex: 100 }}>
        <div className="topbar-inner" style={{ maxWidth: 1100, margin: "0 auto", padding: "0 20px", display: "flex", alignItems: "center", justifyContent: "space-between", height: 58 }}>
          <div className="topbar-left" style={{ display: "flex", alignItems: "center", gap: 24 }}>
            <div onClick={() => setPage("home")} style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 7 }}>
              <div style={{ width: 30, height: 30, borderRadius: 8, background: "linear-gradient(135deg,#7c3aed,#4f46e5)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>
              </div>
              <span style={{ fontWeight: 800, fontSize: 17, color: "#f1f5f9", letterSpacing: -0.5 }}>Music Space</span>
            </div>
            <div style={{ display: "flex", gap: 2 }}>
              {navItems.map(([p,lbl]) => (
                <button key={p} onClick={() => p === "teachers" ? goToTeacherSearch() : setPage(p)} style={{ padding: "5px 12px", borderRadius: 7, border: "none", background: page===p?"#1e293b":"transparent", color: page===p?"#f1f5f9":"#475569", fontSize: 13, fontWeight: page===p?600:400, cursor: "pointer" }}>{lbl}</button>
              ))}
            </div>
          </div>
          <div className="topbar-actions" style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <ModeSwitch mode={appMode} onChange={changeAppMode} labels={{ demo: t("demo"), live: t("live") }} />
            <LanguageSelect language={language} onChange={changeLanguage} label={t("language")} />
            {/* Music indicator in nav */}
            {playing && (
              <div style={{ display: "flex", alignItems: "center", gap: 5, background: "rgba(124,58,237,0.1)", border: "1px solid rgba(124,58,237,0.2)", borderRadius: 20, padding: "4px 10px" }}>
                {[1,2,3].map(i => <div key={i} style={{ width: 2, height: 8+Math.sin(i)*4, background: "#a78bfa", borderRadius: 2, animation: `spin ${0.6+i*0.15}s ease-in-out infinite alternate` }} />)}
                <span style={{ fontSize: 11, color: "#a78bfa", fontWeight: 500 }}>{t("ambient")}</span>
              </div>
            )}
            {!isDemoMode && role ? (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ width: 32, height: 32, borderRadius: "50%", background: role==="teacher"?"#1c1400":"#2e1065", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: role==="teacher"?"#f59e0b":"#a78bfa" }}>{role[0].toUpperCase()}</div>
                <span style={{ fontSize: 13, color: "#94a3b8", fontWeight: 500 }}>{user?.name || role}</span>
                <button onClick={handleLogout} style={{ padding: "5px 10px", border: "1px solid #1e293b", borderRadius: 7, background: "transparent", fontSize: 12, color: "#475569", cursor: "pointer" }}>{t("logOut")}</button>
              </div>
            ) : isDemoMode ? (
              <button onClick={() => changeAppMode("live")} style={{ padding: "7px 14px", border: "1px solid #334155", borderRadius: 9, background: "transparent", fontSize: 13, fontWeight: 700, color: "#cbd5e1", cursor: "pointer" }}>{t("useRealAccounts")}</button>
            ) : (
              <>
                <button onClick={() => { setModal("login"); setLoginMode("login"); }} style={{ padding: "7px 14px", border: "1px solid #1e293b", borderRadius: 9, background: "transparent", fontSize: 13, fontWeight: 500, color: "#94a3b8", cursor: "pointer" }}>{t("logIn")}</button>
                <button onClick={() => { setModal("login"); setLoginMode("signup"); }} style={{ padding: "7px 14px", border: "none", borderRadius: 9, background: "#7c3aed", fontSize: 13, fontWeight: 600, color: "white", cursor: "pointer" }}>{t("getStarted")}</button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* HOME */}
      {page === "home" && (
        <div>
          {/* HERO */}
          <section className="home-hero" style={{ position: "relative", minHeight: "calc(100vh - 58px)", maxHeight: 860, overflow: "hidden", display: "flex", alignItems: "center", padding: "70px 20px 138px", background: "#050816" }}>
            <img src={heroClean} alt="" aria-hidden="true" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "center", display: "block" }} />
            <div style={{ position: "absolute", inset: 0, background: "linear-gradient(90deg, rgba(2,6,23,0.84) 0%, rgba(2,6,23,0.55) 39%, rgba(2,6,23,0.14) 66%, rgba(2,6,23,0.5) 100%)" }} />
            <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(2,6,23,0.24) 0%, rgba(2,6,23,0.1) 42%, rgba(2,6,23,0.82) 100%)" }} />
            <ConstellationCanvas />
            <div className="home-hero-copy" style={{ position: "relative", zIndex: 2, width: "100%", maxWidth: 1100, margin: "0 auto", textAlign: "left" }}>
              <div style={{ maxWidth: 620 }}>
                <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(2,6,23,0.52)", border: "1px solid rgba(134,239,172,0.26)", borderRadius: 20, padding: "6px 14px", marginBottom: 22, backdropFilter: "blur(10px)" }}>
                  <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#22c55e", animation: "pulse 2s infinite" }} />
                  <span style={{ color: "#86efac", fontSize: 12, fontWeight: 500 }}>{isDemoMode ? t("demoMode") : t("liveMode")} · {teachers.filter(t => t.status==="available").length} {t("teachersAvailableNow")}</span>
                </div>
                <h1 style={{ fontSize: "clamp(38px,5.8vw,76px)", fontWeight: 900, color: "white", lineHeight: 1.02, marginBottom: 18, letterSpacing: 0, textShadow: "0 20px 48px rgba(2,6,23,0.62)" }}>
                  {t("heroLineOne")}<br />
                  <span style={{ background: "linear-gradient(90deg,#f0abfc,#38bdf8)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>{t("heroLineTwo")}</span>
                </h1>
                <p style={{ fontSize: "clamp(15px,1.55vw,20px)", color: "#dbeafe", marginBottom: 0, lineHeight: 1.68, textShadow: "0 12px 28px rgba(2,6,23,0.68)" }}>{t("heroCopy")}<br />{t("heroCopyInstruments")}</p>
              </div>
            </div>
            <div className="home-hero-menu" style={{ position: "absolute", zIndex: 3, left: "50%", bottom: 24, transform: "translateX(-50%)", width: "min(1100px, calc(100% - 40px))", display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 12 }}>
              <button onClick={handleStartFree} style={{ minHeight: 74, padding: "14px 18px", borderRadius: 14, border: "1px solid rgba(255,255,255,0.16)", background: "linear-gradient(135deg, rgba(124,58,237,0.94), rgba(6,182,212,0.82))", color: "#ffffff", boxShadow: "0 18px 44px rgba(2,6,23,0.34)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, fontSize: 15, fontWeight: 900 }}>
                <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                {t("startLearningFree")}
              </button>
              <button onClick={handleBrowseTeachers} style={{ minHeight: 74, padding: "14px 18px", borderRadius: 14, border: "1px solid rgba(255,255,255,0.16)", background: "rgba(2,6,23,0.56)", backdropFilter: "blur(14px)", color: "#e0f2fe", boxShadow: "0 18px 44px rgba(2,6,23,0.3)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, fontSize: 15, fontWeight: 850 }}>
                <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                {t("browseTeachers")}
              </button>
              <button onClick={toggleMusic} style={{ minHeight: 74, padding: "14px 18px", borderRadius: 14, border: `1px solid ${playing ? "rgba(34,197,94,0.35)" : "rgba(255,255,255,0.16)"}`, background: playing ? "rgba(34,197,94,0.18)" : "rgba(2,6,23,0.56)", backdropFilter: "blur(14px)", color: playing ? "#bbf7d0" : "#e9d5ff", boxShadow: "0 18px 44px rgba(2,6,23,0.3)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, fontSize: 15, fontWeight: 850 }}>
                <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>
                {playing ? t("ambient") : t("playAmbientMusic")}
              </button>
            </div>
          </section>

          {/* PHOTO GALLERY */}
          <PhotoGallery />

          {/* INSTRUMENTS */}
          <div style={{ background: "#0f172a", borderTop: "1px solid #1e293b", padding: "56px 20px" }}>
            <div style={{ maxWidth: 1000, margin: "0 auto" }}>
              <h2 style={{ fontSize: 24, fontWeight: 800, color: "#f1f5f9", textAlign: "center", marginBottom: 8 }}>{t("browseByInstrument")}</h2>
              <p style={{ textAlign: "center", color: "#475569", marginBottom: 32, fontSize: 14 }}>{t("findRightTeacher")}</p>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 12 }}>
                {INSTRUMENT_OPTIONS.map((name) => (
                  <div key={name} onClick={() => { if (isDemoMode || role) { setFilterI(name); setPage("teachers"); } else setModal("login"); }}
                    style={{ background: "#0b0e16", border: "1px solid #1e293b", borderRadius: 14, padding: 0, textAlign: "left", cursor: "pointer", transition: "all 0.2s", overflow: "hidden" }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor="#7c3aed55"; e.currentTarget.style.transform="translateY(-3px)"; e.currentTarget.style.boxShadow="0 6px 20px rgba(124,58,237,0.15)"; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor="#1e293b"; e.currentTarget.style.transform="none"; e.currentTarget.style.boxShadow="none"; }}>
                    <div style={{ position: "relative", aspectRatio: "4 / 3", overflow: "hidden", background: "#020617" }}>
                      <InstrumentFrameVisual instrument={name} alt={name} />
                      <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(15,23,42,0.04), rgba(15,23,42,0.5))" }} />
                    </div>
                    <div style={{ padding: "12px 12px 14px" }}>
                      <div style={{ fontWeight: 800, fontSize: 13, color: "#f1f5f9", marginBottom: 3 }}>{name}</div>
                      <div style={{ fontSize: 11, color: "#22c55e", fontWeight: 600 }}>{teachers.filter(t => t.instruments.includes(name) && t.status==="available").length} {t("available")}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* HOW IT WORKS */}
          <div style={{ padding: "58px 20px 64px", borderTop: "1px solid #0f172a", background: "#0b0e16" }}>
            <div style={{ maxWidth: 1100, margin: "0 auto" }}>
              <div style={{ textAlign: "center", marginBottom: 30 }}>
                <h2 style={{ fontSize: 28, fontWeight: 850, color: "#f1f5f9", marginBottom: 8 }}>{t("howItWorks")}</h2>
                <p style={{ color: "#64748b", fontSize: 14, lineHeight: 1.6 }}>Zobacz onboarding i cały flow od wyboru nauczyciela po płatność, lekcję albo zwrot.</p>
              </div>

              <div className="how-it-works-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 16, marginBottom: 30 }}>
                {howItWorksSteps.map(([icon,title,desc], index) => (
                  <div key={title} style={{ background: "rgba(15,23,42,0.66)", border: "1px solid rgba(148,163,184,0.14)", borderRadius: 8, padding: "22px 18px", textAlign: "center", minWidth: 0 }}>
                    <div style={{ fontSize: 34, marginBottom: 12 }}>{icon}</div>
                    <div style={{ color: "#7dd3fc", fontSize: 11, fontWeight: 900, marginBottom: 8 }}>STEP {index + 1}</div>
                    <h3 style={{ fontWeight: 800, fontSize: 15, color: "#f1f5f9", marginBottom: 7 }}>{title}</h3>
                    <p style={{ color: "#64748b", fontSize: 13, lineHeight: 1.55 }}>{desc}</p>
                  </div>
                ))}
              </div>

              <div className="onboarding-grid" style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.08fr) minmax(320px, 0.92fr)", gap: 20, alignItems: "start" }}>
                <div style={{ border: "1px solid rgba(148,163,184,0.14)", borderRadius: 8, overflow: "hidden", background: "#020617", boxShadow: "0 20px 58px rgba(2,6,23,0.28)" }}>
                  <video controls playsInline preload="metadata" poster={heroClean} style={{ display: "block", width: "100%", aspectRatio: "16 / 9", objectFit: "cover", background: "#020617" }}>
                    <source src="/media/musicspace-walkthrough-1080p.mp4" type="video/mp4" />
                  </video>
                </div>

                <div style={{ display: "grid", gap: 10 }}>
                  {faqItems.map((item) => {
                    const open = openFaq === item.id;
                    return (
                      <div key={item.id} style={{ background: open ? "rgba(15,23,42,0.84)" : "rgba(15,23,42,0.58)", border: `1px solid ${open ? "rgba(125,211,252,0.32)" : "rgba(148,163,184,0.14)"}`, borderRadius: 8, overflow: "hidden" }}>
                        <button type="button" onClick={() => setOpenFaq(open ? "" : item.id)} aria-expanded={open} style={{ width: "100%", minHeight: 52, padding: "14px 15px", border: "none", background: "transparent", color: "#f8fafc", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14, cursor: "pointer", textAlign: "left", fontSize: 14, fontWeight: 850 }}>
                          <span>{item.question}</span>
                          <span aria-hidden="true" style={{ color: open ? "#7dd3fc" : "#64748b", fontSize: 20, lineHeight: 1 }}>{open ? "−" : "+"}</span>
                        </button>
                        {open && (
                          <div style={{ padding: "0 15px 15px", color: "#94a3b8", fontSize: 13, lineHeight: 1.65 }}>
                            {item.answer}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TEACHERS */}
      {page === "teachers" && (
        <CosmosSection playing={playing} volume={volume}>
          <div style={{ maxWidth: 1100, margin: "0 auto", padding: "34px 20px 48px" }}>
            {(isDemoMode || role) && <LoggedInHero role={role || "student"} onFindTeachers={goToTeacherSearch} onShowProfile={showUserProfile} onPrimaryAction={() => role === "teacher" ? showUserProfile() : openInstrumentBooking(OTHER_INSTRUMENT)} />}
            <div id="find-teachers" style={{ background: "linear-gradient(180deg, rgba(8,15,30,0.9), rgba(9,14,26,0.78))", border: "1px solid rgba(148,163,184,0.14)", borderRadius: 24, padding: 24, backdropFilter: "blur(12px)", marginBottom: 24 }}>
              <h1 style={{ fontSize: 34, lineHeight: 1.05, fontWeight: 800, color: "#f8fafc", marginBottom: 16 }}>Find a Teacher</h1>
              <div style={{ background: isDemoMode ? "rgba(124,58,237,0.1)" : "rgba(34,197,94,0.08)", border: `1px solid ${isDemoMode ? "rgba(167,139,250,0.24)" : "rgba(34,197,94,0.2)"}`, borderRadius: 14, padding: "11px 13px", color: isDemoMode ? "#d8b4fe" : "#86efac", fontSize: 12, lineHeight: 1.5, marginBottom: 16 }}>
                {isDemoMode ? "Demo mode uses sample teachers and sample lessons so the product can be shown without accounts." : `Live mode shows real teacher accounts from the API. ${liveTeacherCount} teacher account${liveTeacherCount === 1 ? "" : "s"} found.`}
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 20 }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.2)", borderRadius: 999, padding: "4px 11px", fontSize: 11, color: "#22c55e", fontWeight: 700 }}>
                  <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#22c55e" }} />
                  {teachers.filter(t => t.status==="available").length} Available Now
                </span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(59,130,246,0.1)", border: "1px solid rgba(96,165,250,0.16)", borderRadius: 999, padding: "4px 11px", fontSize: 11, color: "#93c5fd", fontWeight: 700 }}>
                  {filtered.length} matched teachers
                </span>
              </div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name or instrument..." style={{ flex: "1 1 220px", padding: "11px 13px", border: "1px solid rgba(148,163,184,0.16)", borderRadius: 12, fontSize: 13, background: "rgba(15,23,42,0.75)", color: "#f1f5f9" }} />
                <select value={filterI} onChange={e => setFilterI(e.target.value)} style={{ padding: "11px 13px", border: "1px solid rgba(148,163,184,0.16)", borderRadius: 12, fontSize: 13, background: "rgba(15,23,42,0.75)", color: "#f1f5f9", cursor: "pointer" }}>
                  <option>All</option>{INSTRUMENTS.map(i => <option key={i}>{i}</option>)}
                </select>
                <select value={filterS} onChange={e => setFilterS(e.target.value)} style={{ padding: "11px 13px", border: "1px solid rgba(148,163,184,0.16)", borderRadius: 12, fontSize: 13, background: "rgba(15,23,42,0.75)", color: "#f1f5f9", cursor: "pointer" }}>
                  <option>All</option><option>Available</option>
                </select>
              </div>
            </div>

            {(isDemoMode || role === "student") && (
              <div style={{ marginBottom: 24 }}>
                <h2 style={{ fontSize: 17, fontWeight: 800, color: "#f1f5f9", marginBottom: 12 }}>Choose an instrument</h2>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(130px,1fr))", gap: 10 }}>
                  {INSTRUMENT_OPTIONS.map((name) => {
                    const hasTeachers = teachers.some(t => t.instruments.includes(name));
                    return (
                      <button key={name} onClick={() => { setFilterI(name); setSearch(""); setFilterS("All"); }} style={{ background: filterI === name ? "rgba(124,58,237,0.16)" : "rgba(15,23,42,0.72)", border: `1px solid ${filterI === name ? "rgba(167,139,250,0.38)" : "rgba(148,163,184,0.14)"}`, borderRadius: 14, padding: 0, textAlign: "left", cursor: "pointer", overflow: "hidden" }}>
                        <div style={{ position: "relative", aspectRatio: "4 / 3", overflow: "hidden", background: "#020617" }}>
                          <InstrumentFrameVisual instrument={name} alt={name} />
                          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(15,23,42,0.06), rgba(15,23,42,0.58))" }} />
                        </div>
                        <div style={{ padding: "12px 12px 14px" }}>
                          <div style={{ fontWeight: 800, fontSize: 13, color: "#f1f5f9", marginBottom: 3 }}>{name}</div>
                          <div style={{ fontSize: 11, color: hasTeachers ? "#86efac" : "#93c5fd" }}>{hasTeachers ? `${teachers.filter(t => t.instruments.includes(name)).length} teacher profiles` : "Book without teacher"}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {filtered.length > 0 ? (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: 16 }}>
                {filtered.map(t => <TeacherCard key={t.id} t={t} onConnect={tt => { setConnectT(tt); setModal(isDemoMode ? "demo-connect" : "connect"); }} onBook={openTeacherBooking} onShowCv={setCvTeacher} />)}
              </div>
            ) : (
              <div style={{ background: "rgba(15,23,42,0.72)", border: "1px solid rgba(148,163,184,0.14)", borderRadius: 18, padding: 24, color: "#94a3b8", fontSize: 14 }}>
                <div style={{ marginBottom: filterI !== "All" ? 14 : 0 }}>{isDemoMode ? "No demo teachers match these filters." : filterI !== "All" ? `No teachers for ${filterI} are available yet.` : "No live teacher accounts match these filters yet. Create a teacher account in Live mode, complete the profile, and it will appear here."}</div>
                {filterI !== "All" && (isDemoMode || role === "student") && <button onClick={() => openInstrumentBooking(filterI)} style={{ padding: "11px 14px", background: "#7c3aed", color: "white", border: "none", borderRadius: 10, fontSize: 13, fontWeight: 800, cursor: "pointer" }}>Book {filterI} lesson without choosing a teacher</button>}
              </div>
            )}
          </div>
        </CosmosSection>
      )}

      {/* MY LESSONS */}
      {page === "lessons" && (
        <CosmosSection playing={playing} volume={volume}>
          <div style={{ maxWidth: 1100, margin: "0 auto", padding: "34px 20px 52px" }}>
            {(isDemoMode || role) && (
              <LoggedInHero
                role={role || "student"}
                onFindTeachers={goToTeacherSearch}
                onShowProfile={showUserProfile}
                onPrimaryAction={() => role === "teacher" ? showUserProfile() : openInstrumentBooking(OTHER_INSTRUMENT)}
              />
            )}
            <div style={{ background: "linear-gradient(180deg, rgba(8,15,30,0.9), rgba(9,14,26,0.8))", border: "1px solid rgba(148,163,184,0.14)", borderRadius: 24, padding: 24, backdropFilter: "blur(12px)", marginBottom: 24 }}>
              <h1 style={{ fontSize: 34, lineHeight: 1.05, fontWeight: 800, color: "#f8fafc", marginBottom: 18 }}>My Lessons</h1>
              {isDemoMode ? (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 12 }}>
                  {[["12","Lessons Completed"],["4h","This Month"],["4.8","Avg Rating"]].map(([n,l]) => (
                    <div key={l} style={{ background: "rgba(15,23,42,0.72)", borderRadius: 16, border: "1px solid rgba(148,163,184,0.14)", padding: "14px 16px" }}>
                      <div style={{ fontSize: 26, fontWeight: 800, color: "#f1f5f9", marginBottom: 2 }}>{n}</div>
                      <div style={{ fontSize: 12, color: "#64748b" }}>{l}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.2)", borderRadius: 14, padding: "13px 15px", color: "#86efac", fontSize: 13 }}>
                  {trialAvailable ? "Your free trial lesson is available." : "Your free trial lesson has been used. Admin can reset it."} Requests without a teacher stay pending while Music Space looks for someone to accept.
                </div>
              )}
            </div>

            <div style={{ background: "linear-gradient(180deg, rgba(8,15,30,0.84), rgba(9,14,26,0.78))", border: "1px solid rgba(148,163,184,0.14)", borderRadius: 24, padding: 22, backdropFilter: "blur(12px)" }}>
              {isDemoMode ? (
                <>
                  <h2 style={{ fontSize: 17, fontWeight: 700, color: "#f1f5f9", marginBottom: 12 }}>Upcoming</h2>
                  {LESSONS.filter(l => l.status==="upcoming").map(l => (<div key={l.id} style={{ background: "rgba(15,23,42,0.72)", border: "1px solid rgba(148,163,184,0.12)", borderRadius: 16, padding: 16, marginBottom: 10, display: "flex", justifyContent: "space-between", alignItems: "center" }}><div><div style={{ fontWeight: 600, fontSize: 14, color: "#f1f5f9" }}>{l.title}</div><div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>with {l.teacher} · {l.date} at {l.time}</div></div><div style={{ display: "flex", gap: 7, alignItems: "center" }}><span style={{ background: "#1e3a5f", color: "#60a5fa", padding: "3px 8px", borderRadius: 20, fontSize: 11, fontWeight: 600 }}>{l.instrument}</span><button onClick={() => startDirectRoom({ id: `lesson-${l.id}`, name: l.teacher, av: l.teacher.split(" ").map(part => part[0]).join("").slice(0,2).toUpperCase(), instruments: [l.instrument] })} style={{ padding: "7px 12px", background: "#7c3aed", color: "white", border: "none", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Join</button></div></div>))}
                  <h2 style={{ fontSize: 17, fontWeight: 700, color: "#f1f5f9", marginBottom: 12, marginTop: 22 }}>Past Lessons</h2>
                  {LESSONS.filter(l => l.status==="completed").map(l => (<div key={l.id} style={{ background: "rgba(15,23,42,0.52)", border: "1px solid rgba(148,163,184,0.1)", borderRadius: 16, padding: 16, marginBottom: 10, display: "flex", justifyContent: "space-between", alignItems: "center", opacity: 0.72 }}><div><div style={{ fontWeight: 600, fontSize: 13, color: "#cbd5e1" }}>{l.title}</div><div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>with {l.teacher} · {l.date}</div></div><span style={{ background: "rgba(34,197,94,0.08)", color: "#22c55e", padding: "3px 8px", borderRadius: 20, fontSize: 11, fontWeight: 600 }}>Done</span></div>))}
                </>
              ) : (
                studentBookings.length > 0 ? (
                  <>
                    <h2 style={{ fontSize: 17, fontWeight: 700, color: "#f1f5f9", marginBottom: 12 }}>Bookings</h2>
                    {studentBookings.map(booking => <LessonBookingCard key={booking.id} booking={booking} role="student" onCancel={cancelStudentBooking} />)}
                  </>
                ) : (
                  <div style={{ color: "#94a3b8", fontSize: 14, lineHeight: 1.6 }}>
                    No real lessons have been saved for this account yet.
                  </div>
                )
              )}
            </div>
          </div>
        </CosmosSection>
      )}

      {/* TEACHER DASHBOARD */}
      {page === "dashboard" && role === "teacher" && (
        <div style={{ maxWidth: 900, margin: "0 auto", padding: "28px 20px" }}>
          {!isDemoMode && <LoggedInHero role={role} ctaLabel="Edit profile" onFindTeachers={goToTeacherSearch} onShowProfile={showUserProfile} onPrimaryAction={showUserProfile} />}
          <h1 style={{ fontSize: 24, fontWeight: 800, color: "#f1f5f9", marginBottom: 4 }}>Teacher Dashboard</h1>
          <p style={{ color: "#475569", fontSize: 13, marginBottom: 22 }}>Music Marina Foundation – Teacher Portal</p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12, marginBottom: 24 }}>
            {[["$1,240","Revenue"],["16","Lessons"],["4.9","Rating"],["94%","Acceptance"]].map(([n,l]) => (<div key={l} style={{ background: "#0f172a", borderRadius: 12, border: "1px solid #1e293b", padding: "14px 16px" }}><div style={{ fontSize: 22, fontWeight: 800, color: "#f1f5f9", marginBottom: 2 }}>{n}</div><div style={{ fontSize: 12, color: "#475569" }}>{l}</div></div>))}
          </div>
          <div id="user-profile">
            <TeacherProfilePanel profile={teacherProfile} onSave={saveTeacherProfile} saving={teacherProfileBusy} saveError={teacherProfileError} />
          </div>
          <TeacherDashboard
            token={token}
            available={teacherProfile?.status === "available"}
            lessons={[]}
            onAvailabilityChange={updateTeacherAvailability}
            onAcceptRequest={({ request, roomId }) => {
              openSession({
                id: request.teacherId,
                name: request.teacherName,
                av: request.teacherName.split(" ").map((part) => part[0]).join("").slice(0, 2).toUpperCase(),
                instruments: [request.topic.split(" ")[0] || "Music"],
              }, roomId);
            }}
          />
        </div>
      )}

      {/* ADMIN */}
      {page === "admin" && role === "admin" && (
        <div style={{ maxWidth: 900, margin: "0 auto", padding: "28px 20px" }}>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: "#f1f5f9", marginBottom: 22 }}>Admin Panel</h1>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12, marginBottom: 24 }}>
            {[["247","Students"],["38","Teachers"],["1,842","Lessons/mo"],["$24,680","Revenue"]].map(([n,l]) => (<div key={l} style={{ background: "#0f172a", borderRadius: 12, border: "1px solid #1e293b", padding: "14px 16px" }}><div style={{ fontSize: 22, fontWeight: 800, color: "#f1f5f9", marginBottom: 2 }}>{n}</div><div style={{ fontSize: 12, color: "#475569" }}>{l}</div></div>))}
          </div>
          <div style={{ background: "#0f172a", borderRadius: 14, border: "1px solid #1e293b", padding: 20, marginBottom: 16 }}>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: "#f1f5f9", marginBottom: 14 }}>Pending Approvals</h3>
            {[{name:"Piotr Malinowski",instrument:"Piano"},{name:"Natalia Zając",instrument:"Violin"}].map(t => (<div key={t.name} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0", borderBottom: "1px solid #0f172a" }}><div><div style={{ fontWeight: 600, fontSize: 13, color: "#f1f5f9" }}>{t.name}</div><div style={{ fontSize: 12, color: "#475569" }}>{t.instrument} Teacher</div></div><div style={{ display: "flex", gap: 7 }}><button style={{ padding: "6px 12px", background: "rgba(34,197,94,0.1)", color: "#22c55e", border: "1px solid rgba(34,197,94,0.2)", borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Approve</button><button style={{ padding: "6px 12px", background: "rgba(239,68,68,0.08)", color: "#ef4444", border: "1px solid rgba(239,68,68,0.2)", borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Reject</button></div></div>))}
          </div>
          <div style={{ background: "#0f172a", borderRadius: 14, border: "1px solid #1e293b", padding: 20, marginBottom: 16 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:10, marginBottom:14 }}>
              <h3 style={{ fontSize: 15, fontWeight: 700, color: "#f1f5f9" }}>Student Trial Lessons</h3>
              <button onClick={loadAdminStudents} style={{ padding:"7px 10px", background:"transparent", color:"#94a3b8", border:"1px solid #1e293b", borderRadius:8, fontSize:12, fontWeight:700, cursor:"pointer" }}>Refresh</button>
            </div>
            {adminError && <div style={{ background:"rgba(239,68,68,0.08)", border:"1px solid rgba(239,68,68,0.22)", borderRadius:10, padding:"9px 11px", color:"#fca5a5", fontSize:12, marginBottom:12 }}>{adminError}</div>}
            {adminStudents.length > 0 ? adminStudents.map(student => (
              <div key={student.id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:12, padding:"12px 0", borderBottom:"1px solid #0f172a", flexWrap:"wrap" }}>
                <div>
                  <div style={{ fontWeight:700, fontSize:13, color:"#f1f5f9" }}>{student.name}</div>
                  <div style={{ fontSize:12, color:"#64748b", marginTop:2 }}>{student.email} · {student.bookingsCount} bookings</div>
                </div>
                <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                  <span style={{ background:student.trialUsed?"rgba(239,68,68,0.1)":"rgba(34,197,94,0.1)", color:student.trialUsed?"#fca5a5":"#86efac", padding:"4px 9px", borderRadius:20, fontSize:11, fontWeight:800 }}>{student.trialUsed ? "Trial used" : "Trial available"}</span>
                  <button onClick={() => resetStudentTrial(student.id)} style={{ padding:"7px 10px", background:"rgba(59,130,246,0.1)", color:"#93c5fd", border:"1px solid rgba(96,165,250,0.2)", borderRadius:8, fontSize:12, fontWeight:800, cursor:"pointer" }}>Reset trial</button>
                </div>
              </div>
            )) : <div style={{ color:"#64748b", fontSize:13 }}>No student accounts yet.</div>}
          </div>
          <div style={{ background: "#0f172a", borderRadius: 14, border: "1px solid #1e293b", padding: 20 }}>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: "#f1f5f9", marginBottom: 14 }}>All Teachers ({teachers.length})</h3>
            {teachers.map(t => (<div key={t.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid #0f172a" }}><div style={{ display: "flex", alignItems: "center", gap: 10 }}><div style={{ width: 32, height: 32, borderRadius: "50%", background: "#1e293b", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#64748b" }}>{t.av}</div><div><div style={{ fontWeight: 600, fontSize: 13, color: "#f1f5f9" }}>{t.name}</div><div style={{ fontSize: 11, color: "#475569" }}>{t.instruments.join(", ")}</div></div></div><StatusBadge status={t.status}/></div>))}
          </div>
        </div>
      )}

      {/* LOGIN MODAL */}
      {modal === "login" && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 998, padding: 20 }} onClick={e => { if (e.target===e.currentTarget) setModal(null); }}>
          <form onSubmit={handleAuthSubmit} style={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 20, padding: 24, width: "100%", maxWidth: 400, maxHeight: "90vh", overflowY: "auto", animation: "slideUp 0.2s ease" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
              <h2 style={{ fontSize: 18, fontWeight: 700, color: "#f1f5f9" }}>{loginMode==="forgot"?"Reset password":loginMode==="reset"?"Set new password":loginMode==="login"?"Welcome back":"Create account"}</h2>
              <button type="button" onClick={() => setModal(null)} style={{ background: "none", border: "none", fontSize: 20, color: "#475569", cursor: "pointer" }}>×</button>
            </div>
            <div style={{ display: "flex", background: "#1e293b", borderRadius: 9, padding: 3, marginBottom: 18 }}>
              {["login","signup"].map(m => (<button type="button" key={m} onClick={() => { setLoginMode(m); setAuthSubmitError(""); setAuthNotice(""); setCaptchaToken(""); }} style={{ flex: 1, padding: "7px", border: "none", borderRadius: 7, background: loginMode===m?"#0f172a":"transparent", fontSize: 13, fontWeight: loginMode===m?600:400, color: loginMode===m?"#f1f5f9":"#475569", cursor: "pointer" }}>{m==="login"?"Log In":"Sign Up"}</button>))}
            </div>
            {loginMode === "signup" && (<div style={{ marginBottom: 10 }}><label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#64748b", marginBottom: 5 }}>Full Name</label><input value={authForm.name} onChange={e => setAuthForm(form => ({ ...form, name: e.target.value }))} required minLength={2} placeholder="Your name" style={{ width: "100%", padding: "9px 11px", border: "1px solid #1e293b", borderRadius: 8, fontSize: 13, background: "#1e293b", color: "#f1f5f9" }} /></div>)}
            {loginMode !== "reset" && <div style={{ marginBottom: 10 }}><label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#64748b", marginBottom: 5 }}>Email</label><input value={authForm.email} onChange={e => setAuthForm(form => ({ ...form, email: e.target.value }))} type="email" required placeholder="your@email.com" style={{ width: "100%", padding: "9px 11px", border: "1px solid #1e293b", borderRadius: 8, fontSize: 13, background: "#1e293b", color: "#f1f5f9" }} /></div>}
            {loginMode === "reset" && <div style={{ marginBottom: 10 }}><label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#64748b", marginBottom: 5 }}>Reset token</label><input value={resetForm.token} onChange={e => setResetForm(form => ({ ...form, token: e.target.value }))} required placeholder="Paste reset token" style={{ width: "100%", padding: "9px 11px", border: "1px solid #1e293b", borderRadius: 8, fontSize: 13, background: "#1e293b", color: "#f1f5f9" }} /></div>}
            {loginMode !== "forgot" && <div style={{ marginBottom: 10 }}><label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#64748b", marginBottom: 5 }}>{loginMode === "reset" ? "New password" : "Password"}</label><input value={loginMode === "reset" ? resetForm.password : authForm.password} onChange={e => loginMode === "reset" ? setResetForm(form => ({ ...form, password: e.target.value })) : setAuthForm(form => ({ ...form, password: e.target.value }))} type="password" minLength={8} required placeholder="••••••••" style={{ width: "100%", padding: "9px 11px", border: "1px solid #1e293b", borderRadius: 8, fontSize: 13, background: "#1e293b", color: "#f1f5f9" }} /></div>}
            {loginMode === "login" && <button type="button" onClick={() => { setLoginMode("forgot"); setAuthSubmitError(""); setAuthNotice(""); }} style={{ background: "transparent", border: "none", color: "#93c5fd", fontSize: 12, fontWeight: 700, cursor: "pointer", marginBottom: 14, padding: 0 }}>Forgot password?</button>}
            {loginMode === "forgot" && <div style={{ color: "#94a3b8", fontSize: 12, lineHeight: 1.5, marginBottom: 14 }}>Enter your email and we will send a password reset link if the account exists.</div>}
            {loginMode === "reset" && <div style={{ color: "#94a3b8", fontSize: 12, lineHeight: 1.5, marginBottom: 14 }}>Set a new password using the reset token from your email.</div>}
            {loginMode !== "reset" && (
              <button type="button" disabled style={{ width: "100%", padding: "10px 12px", border: "1px solid #334155", borderRadius: 10, background: "rgba(15,23,42,0.72)", color: "#64748b", fontSize: 13, fontWeight: 800, cursor: "not-allowed", marginBottom: 12, display: "flex", alignItems: "center", justifyContent: "center", gap: 9 }}>
                <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
                  <path fill="#4285F4" d="M21.6 12.23c0-.78-.07-1.53-.2-2.23H12v4.22h5.38a4.6 4.6 0 0 1-1.99 3.02v2.51h3.23c1.89-1.74 2.98-4.31 2.98-7.52Z" />
                  <path fill="#34A853" d="M12 22c2.7 0 4.96-.89 6.62-2.41l-3.23-2.51c-.9.6-2.04.95-3.39.95-2.6 0-4.8-1.76-5.59-4.12H3.07v2.59A10 10 0 0 0 12 22Z" />
                  <path fill="#FBBC05" d="M6.41 13.91a6.02 6.02 0 0 1 0-3.82V7.5H3.07a10 10 0 0 0 0 9l3.34-2.59Z" />
                  <path fill="#EA4335" d="M12 5.97c1.47 0 2.79.51 3.83 1.5l2.86-2.86C16.95 2.99 14.69 2 12 2a10 10 0 0 0-8.93 5.5l3.34 2.59C7.2 7.73 9.4 5.97 12 5.97Z" />
                </svg>
                Continue with Google - coming soon
              </button>
            )}
            {loginMode === "signup" && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#64748b", marginBottom: 8 }}>Join as...</label>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  {[["student","🎓","Student","Learn from experts"],["teacher","🎸","Teacher","Teach and earn"]].map(([r,icon,lbl,desc]) => (<button type="button" key={r} onClick={() => setAuthForm(form => ({ ...form, role: r }))} style={{ padding: 12, border: `2px solid ${authForm.role===r?"#7c3aed":"#1e293b"}`, borderRadius: 10, cursor: "pointer", textAlign: "center", background: authForm.role===r?"#2e1065":"transparent" }}><div style={{ fontSize: 22, marginBottom: 4 }}>{icon}</div><div style={{ fontWeight: 700, fontSize: 13, color: "#f1f5f9" }}>{lbl}</div><div style={{ fontSize: 11, color: "#475569" }}>{desc}</div></button>))}
                </div>
              </div>
            )}
            {loginMode === "signup" && authForm.role === "teacher" && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#64748b", marginBottom: 5 }}>Primary instrument</label>
                <select value={authForm.instrument} onChange={e => setAuthForm(form => ({ ...form, instrument: e.target.value }))} style={{ width: "100%", padding: "9px 11px", border: "1px solid #1e293b", borderRadius: 8, fontSize: 13, background: "#1e293b", color: "#f1f5f9" }}>
                  {INSTRUMENTS.map((instrument) => <option key={instrument}>{instrument}</option>)}
                </select>
              </div>
            )}
            <TurnstileBox action={`auth-${loginMode}`} onVerify={setCaptchaToken} />
            {authNotice && <div style={{ background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.22)", borderRadius: 10, padding: "9px 11px", color: "#86efac", fontSize: 12, marginBottom: 12 }}>{authNotice}</div>}
            {authSubmitError && <div style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.22)", borderRadius: 10, padding: "9px 11px", color: "#fca5a5", fontSize: 12, marginBottom: 12 }}>{authSubmitError}</div>}
            <button type="submit" disabled={authBusy || authLoading} style={{ width: "100%", padding: "12px", background: authBusy || authLoading ? "#334155" : "#7c3aed", color: "white", border: "none", borderRadius: 10, fontWeight: 700, cursor: authBusy || authLoading ? "wait" : "pointer" }}>{authBusy ? "Please wait..." : loginMode==="forgot" ? "Send reset link" : loginMode==="reset" ? "Update password" : loginMode==="login" ? "Log in" : "Create account"}</button>
            {["forgot","reset"].includes(loginMode) && <button type="button" onClick={() => { setLoginMode("login"); setAuthSubmitError(""); setAuthNotice(""); }} style={{ width: "100%", padding: "9px", background: "transparent", color: "#94a3b8", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer", marginTop: 8 }}>Back to log in</button>}
          </form>
        </div>
      )}

      {modal === "connect" && connectT && <ConnectFlow teacher={connectT} token={token} onClose={() => { setModal(null); setConnectT(null); }} onAccepted={(teacher, roomId) => { setModal(null); setConnectT(null); openSession(teacher, roomId); }} />}
      {modal === "demo-connect" && connectT && <DemoConnectFlow teacher={connectT} onClose={() => { setModal(null); setConnectT(null); }} onAccepted={(teacher, roomId) => { setModal(null); setConnectT(null); openSession(teacher, roomId); }} />}
      {modal === "book" && (bookT || bookInstrument) && <BookModal teacher={bookT} instrument={bookInstrument} token={token} isDemoMode={isDemoMode} trialAvailable={isDemoMode ? true : trialAvailable} onBooked={(booking) => { setStudentBookings(current => current.some(item => item.id === booking.id) ? current : [...current, booking]); if (booking.isTrial) setTrialAvailable(false); }} onClose={() => { setModal(null); setBookT(null); setBookInstrument(null); }} />}
      {modal === "user-profile" && <UserProfileModal user={user} role={role} onClose={() => setModal(null)} />}
      {cvTeacher && <TeacherCvModal teacher={cvTeacher} onClose={() => setCvTeacher(null)} />}
    </div>
  );
}
